import React, { useEffect, useRef, useState, useCallback, useMemo, memo, forwardRef, useImperativeHandle } from 'react';
import { Save, Trash2, RotateCcw, Plus, ChevronDown, ChevronUp, Info as InfoIcon } from 'lucide-react';
import { Tooltip, message } from 'antd';
import { TOOLTIP_CONFIG, getTooltipContent } from './tooltipConfig';
import {
  C, CollapsibleSection, StatusChip, Pill, Divider, FieldLabel, TextInput, TextArea, JdSelect,
} from '../../components/ui';
import UploadModal from '../../../Library/components/UploadModal';
import type { LibraryFile } from 'mock/folder.data';
import '../../../Library/styles.scss';
import { DailyPlanData, EcoCalendarEvent, FormFieldConfig, Journal, PhysicalHealthTags, RatingLevel, SetupType, TradingSetup, UILabels_DailyPlan, UpdateDailyJournalPayload, PostTradeTooltipData, StepRefHandle, SectionHandle, IncompleteSection } from './type';
import { getFormConfigsField, uploadFieldData_daily_plan_data, uploadFieldData_Setup, mapTCApiToUi, mapTCUiToApi } from './utility_function';
import { updateDailyChecklist, uploadPicture, streamingUploadPicture, getEcoCalendar, getToolTip } from './function';
import { API_URL } from 'env';
import { t } from 'i18next';
import i18next from 'i18next';

// ==========================================
// CONSTANTS & HELPERS (Outside Render Scope)
// ==========================================

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const LEVEL_META: Record<string, { t: string; bg: string; bd: string; tx: string; dot: string }> = {
  high: { t: 'Cao', bg: C.redBg, bd: C.redBd, tx: C.redTx, dot: C.red },
  medium: { t: 'Trung bình', bg: C.goldBg, bd: C.goldBd, tx: C.goldDark, dot: C.gold },
  low: { t: 'Thấp', bg: C.greenBg, bd: C.greenBd, tx: C.greenTx, dot: C.green },
};

const RATING_MAP: Record<string, number> = {
  'Kém': 1, 'Tệ': 1, 'Yếu': 2, 'Tạm ổn': 3, 'Ổn': 3, 'Tốt': 4, 'Rất tốt': 5, 'Tuyệt vời': 5,
};

const BAD_MOOD_LIST = ['Lo lắng', 'Tham lam', 'Sợ hãi', 'Mệt mỏi', 'FOMO', 'Buồn', 'Chán', 'Muốn gỡ lỗ', 'Cay cú sau chuỗi thua', 'Mất tập trung', 'Hưng phấn sau chuỗi thắng', 'Muốn đánh cược', 'Buồn ngủ'];

const PALETTE = [
  [C.green, C.greenBg, C.greenBd, C.greenTx],
  [C.gold, C.goldBg, C.goldBd, C.goldDark],
  [C.infoAc, C.infoBg, C.infoBd, C.infoTx],
  [C.purpleAc, C.purpleBg, C.purpleBd, C.purpleTx],
];

const getOptionLabel = (option: any, lang: string): string => {
  if (typeof option === 'string') return option;
  if (!option) return '';
  if (lang === 'en' || lang === 'en-US') {
    return option.label_en || option.lable_en || option.label_vi || option.lable_vn || option.value || '';
  }
  return option.label_vi || option.lable_vn || option.label_en || option.lable_en || option.value || '';
};

const getOptionValue = (option: any): string => {
  if (typeof option === 'string') return option;
  if (!option) return '';
  return option.value !== undefined ? String(option.value) : (option.label_vi || option.lable_vn || '');
};

const getTagNameByRating = (rating: number, options: any[], lang: string): string => {
  if (rating === 0 || !options.length) return '';
  const item = options[rating - 1];
  return getOptionLabel(item, lang) || getOptionValue(item) || 'Tốt';
};

function formatLocalTime(isoString: string): string {
  if (!isoString) return '--:--';
  try {
    const utcString = isoString.endsWith('Z') ? isoString : `${isoString}Z`;
    const dateObj = new Date(utcString);
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${year}/${month}/${day} - ${hours}:${minutes}`;
  } catch {
    return isoString;
  }
}

function formatUtcTime(isoString: string): string {
  if (!isoString) return '--:--';
  try {
    const utcString = isoString.endsWith('Z') ? isoString : `${isoString}Z`;
    const dateObj = new Date(utcString);
    const year = dateObj.getUTCFullYear();
    const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getUTCDate()).padStart(2, '0');
    const hours = String(dateObj.getUTCHours()).padStart(2, '0');
    const minutes = String(dateObj.getUTCMinutes()).padStart(2, '0');
    return `${year}/${month}/${day} - ${hours}:${minutes}`;
  } catch {
    return isoString;
  }
}

// ==========================================
// CUSTOM HOOKS
// ==========================================

function useSaveStatus(autoResetMs = 2000) {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  // 1. Change your ref definition to use ReturnType<typeof setTimeout>
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 2. Your useCallback function works without any changes now!
  const triggerStatus = useCallback((status: SaveStatus) => {
    setSaveStatus(status);
    if (timerRef.current) clearTimeout(timerRef.current);

    if (status === 'saved' || status === 'error') {
      timerRef.current = setTimeout(() => {
        setSaveStatus('idle');
      }, autoResetMs);
    }
  }, [autoResetMs]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return [saveStatus, triggerStatus] as const;
}

function useIsMounted() {
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);
  return isMounted;
}

// ==========================================
// REUSABLE UI COMPONENTS
// ==========================================

const SaveResetFooter = memo(({
  saveStatus,
  onReset,
  onSave
}: {
  saveStatus: SaveStatus;
  onReset: () => void;
  onSave: () => void;
}) => {
  const isSaving = saveStatus === 'saving';
  const isSaved = saveStatus === 'saved';
  const isError = saveStatus === 'error';

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 24, paddingTop: 18, borderTop: `1px solid ${C.divider}` }}>
      <button
        onClick={onReset}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 14px', borderRadius: 9,
          border: `1px solid ${C.borderSoft}`, background: C.surface, color: C.faint, fontSize: 13,
          fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        <RotateCcw size={14} /> {t('Reset')}
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button
          onClick={onSave}
          disabled={isSaving}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 22px', borderRadius: 10,
            border: 'none',
            background: isSaved ? C.green : isError ? C.red : C.gold,
            color: isSaved || isError ? '#fff' : '#0B1521',
            fontSize: 13.5, fontWeight: 800,
            cursor: isSaving ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
            transition: 'background .2s, color .2s',
          }}
        >
          {isSaving && (
            <svg
              style={{ animation: 'spin 1s linear infinite' }}
              width="15" height="15" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5"
            >
              <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
              <path d="M12 2a10 10 0 0 1 10 10" />
            </svg>
          )}
          {isSaved && (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          )}
          {isError && (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          )}
          {saveStatus === 'idle' && <Save size={15} />}

          {isSaving && t('Saving...')}
          {isSaved && t('Saved!')}
          {isError && t('Error!')}
          {saveStatus === 'idle' && t('Save')}
        </button>
      </div>
    </div>
  );
});

const DynamicOptionGroup = memo(({ label, options, selectedValue, onChange, currentLanguage, tooltipKey, tooltips, hasError }: {
  label: string; options: any[]; selectedValue: string | null; onChange: (val: string) => void; currentLanguage: string;
  tooltipKey?: string; tooltips?: PostTradeTooltipData | null; hasError?: boolean;
}) => {
  const ttContent = tooltipKey ? getTooltipContent(tooltips, tooltipKey) : null;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 11 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: hasError ? '#ef4444' : C.title, margin: 0 }}>{label}</div>
        {ttContent && (
          <Tooltip title={ttContent.text} placement={ttContent.placement || "top"}>
            <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
          </Tooltip>
        )}
      </div>
      <div style={{
        display: 'flex', gap: 10, flexWrap: 'wrap',
        padding: hasError ? '6px' : '0',
        borderRadius: 12,
        border: hasError ? '1.5px solid #ef4444' : '1.5px solid transparent',
        transition: 'all .2s'
      }}>
        {options.map((opt, idx) => {
          const val = getOptionValue(opt);
          const optLabel = getOptionLabel(opt, currentLanguage);

          // Ưu tiên đọc từ option_type hoặc type / kind, fallback theo index
          const type = opt.option_type || opt.type || opt.kind || (idx === 0 ? 'success' : 'danger');
          const isSelected = selectedValue === val;

          // Hàm xác định bảng màu dựa theo option_type: success | warning | danger
          const getColorScheme = (t: string) => {
            switch (t?.toLowerCase()) {
              case 'success':
              case 'pos':
              case 'good':
              case 'ok':
                return { bd: C.green, bg: C.greenBg, tx: C.greenTx };

              case 'warning':
              case 'warn':
                return { bd: C.gold, bg: C.goldBg, tx: C.goldDark };

              case 'danger':
              case 'error':
              case 'neg':
              case 'bad':
              default:
                return { bd: C.red, bg: C.redBg, tx: C.redTx };
            }
          };

          const c = getColorScheme(type);
          const isPos = type === 'success' || type === 'pos' || type === 'good' || type === 'ok';

          return (
            <button
              key={val || idx}
              onClick={() => onChange(val)}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9, padding: '11px 20px', borderRadius: 11,
                fontSize: 13.5, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit',
                border: `1.5px solid ${isSelected ? c.bd : C.chipOffBd}`, background: isSelected ? c.bg : C.surface,
                color: isSelected ? c.tx : C.chipOffTx, fontWeight: isSelected ? 700 : 600,
              }}
            >
              <span style={{
                width: 18, height: 18, flex: 'none', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: isSelected ? c.bd : C.surface, border: isSelected ? 'none' : `1.6px solid ${C.checkOffBd}`
              }}>
                {isSelected && (isPos
                  ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 6" /></svg>
                  : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M18 6L6 18" /></svg>)}
              </span>
              {optLabel}
            </button>
          );
        })}
      </div>
    </div>
  );
});

const PercentField = memo(({ label, value, onChange, placeholder, suffix, onBlur, tooltipKey, tooltips, hasError }: {
  label: string; value: string; onChange: (v: string) => void; placeholder: string; suffix?: string; onBlur?: () => void;
  tooltipKey?: string; tooltips?: PostTradeTooltipData | null; hasError?: boolean;
}) => {
  const ttContent = tooltipKey ? getTooltipContent(tooltips, tooltipKey) : null;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <FieldLabel style={hasError ? { color: '#ef4444' } : undefined}>{label}</FieldLabel>
        {ttContent && (
          <Tooltip title={ttContent.text} placement={ttContent.placement || "top"}>
            <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
          </Tooltip>
        )}
      </div>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <TextInput type="number" step="0.1" value={value}
          onBlur={onBlur}
          onChange={e => onChange(e.target.value)} placeholder={placeholder}
          style={{
            fontWeight: 700, fontSize: 14.5,
            padding: suffix ? '12px 38px 12px 13px' : '12px 13px',
            border: hasError ? '1.5px solid #ef4444' : `1px solid ${C.border}`,
            ...(hasError && { boxShadow: '0 0 0 1px #ef4444' })
          }} />
        {suffix && <span style={{ position: 'absolute', right: 14, fontSize: 13, fontWeight: 700, color: C.uppercaseFaint, pointerEvents: 'none' }}>{suffix}</span>}
      </div>
    </div>
  );
});

const HealthVerdict = memo(({ good, showWarn, allCond }: { good: boolean; showWarn: boolean; allCond: boolean }) => {
  const warn = showWarn && !good;
  const box = warn ? { bg: C.warnBg, bd: C.warnBd } : { bg: C.okSoftBg, bd: C.okSoftBd };
  const icon = warn ? { bg: C.warnIconBg, tx: C.redTx } : { bg: C.okIconBg, tx: C.greenTx };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '15px 17px', borderRadius: 13, background: box.bg, border: `1px solid ${box.bd}` }}>
      <div style={{ width: 38, height: 38, flex: 'none', borderRadius: 11, background: icon.bg, color: icon.tx, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {warn
          ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></svg>
          : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 6" /></svg>}
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: warn ? C.redTx : C.greenTx }}>{warn ? 'Nên thận trọng / giảm rủi ro' : 'Sẵn sàng giao dịch'}</div>
        <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>
          {warn ? 'Thể trạng hoặc tâm lý chưa tối ưu — cân nhắc giảm khối lượng hoặc nghỉ.' : allCond ? 'Điều kiện làm việc & tâm lý ổn định — đủ điều kiện vào phiên.' : 'Hoàn tất đánh giá để xác nhận trạng thái.'}
        </div>
      </div>
    </div>
  );
});

const MarketSelect = memo(({
  label, value, onChange, options, currentLanguage, tooltipKey, tooltips, hasError
}: {
  label: string; value: string; onChange: (v: string) => void; options: any[]; currentLanguage: string;
  tooltipKey?: string; tooltips?: PostTradeTooltipData | null; hasError?: boolean;
}) => {
  const selectOptions = useMemo(() => (options || []).map(opt => ({
    value: getOptionValue(opt),
    label: getOptionLabel(opt, currentLanguage)
  })), [options, currentLanguage]);

  const ttContent = tooltipKey ? getTooltipContent(tooltips, tooltipKey) : null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <FieldLabel style={{ fontSize: 13, color: hasError ? '#ef4444' : C.title, margin: 0 }}>{label}</FieldLabel>
        {ttContent && (
          <Tooltip title={ttContent.text} placement={ttContent.placement || "top"}>
            <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
          </Tooltip>
        )}
      </div>
      <div style={hasError ? { border: '1.5px solid #ef4444', borderRadius: 11 } : undefined}>
        <JdSelect value={value} onChange={onChange} items={selectOptions} />
      </div>
    </div>
  );
});

const PriceField = memo(({
  label, prefix, prefixColor, value, onChange, onBlur, onKeyDown, placeholder, tooltipKey, tooltips, hasError
}: {
  label: string; prefix: string; prefixColor: string; value: string; onChange: (v: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder: string; tooltipKey?: string; tooltips?: PostTradeTooltipData | null; hasError?: boolean;
}) => {
  const ttContent = tooltipKey ? getTooltipContent(tooltips, tooltipKey) : null;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <FieldLabel style={{ fontSize: 13, color: hasError ? '#ef4444' : C.title }}>{label}</FieldLabel>
        {ttContent && (
          <Tooltip title={ttContent.text} placement={ttContent.placement || "top"}>
            <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
          </Tooltip>
        )}
      </div>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <span style={{ position: 'absolute', left: 13, fontSize: 13, fontWeight: 700, color: prefixColor, pointerEvents: 'none' }}>{prefix}</span>
        <TextInput
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          onBlur={e => {
            if (hasError) e.currentTarget.style.borderColor = '#ef4444';
            onBlur?.(e);
          }}
          onFocus={e => {
            if (hasError) e.currentTarget.style.borderColor = '#ef4444';
          }}
          onKeyDown={onKeyDown}
          style={{
            fontWeight: 700,
            padding: '11px 12px 11px 40px',
            border: hasError ? '1.5px solid #ef4444' : `1px solid ${C.border}`,
            ...(hasError && { boxShadow: '0 0 0 1px #ef4444' })
          }}
        />
      </div>
    </div>
  );
});

const ScenarioImage = memo(({ img, onUpload, onClear }: { img?: string; onUpload: (v: string) => void; onClear: () => void }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  const [loadingImg, setLoadingImg] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const loadStream = async (id: number) => {
      setLoadingImg(true);
      try {
        const res = await streamingUploadPicture(id);
        if (res.ok && isMounted) {
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
          objectUrlRef.current = url;
          setDisplayUrl(url);
        }
      } catch (err) {
        console.error("Error streaming image:", err);
      } finally {
        if (isMounted) setLoadingImg(false);
      }
    };

    if (img && !isNaN(Number(img))) {
      loadStream(Number(img));
    } else if (img) {
      setDisplayUrl(img);
    } else {
      setDisplayUrl(null);
    }

    return () => { isMounted = false; };
  }, [img]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  const handleSuccess = useCallback(async (_file: LibraryFile, rawFile: File) => {
    try {
      const resp = await uploadPicture(rawFile);
      if (resp.status) {
        onUpload(resp.data.id.toString());
      }
    } catch (error) {
      console.error("Lỗi khi upload ảnh:", error);
    }
  }, [onUpload]);

  return (
    <>
      {displayUrl ? (
        <div style={{ position: 'relative', width: 150, height: 90, borderRadius: 11, overflow: 'hidden', border: `1px solid ${C.border}`, opacity: loadingImg ? 0.6 : 1 }}>
          <img src={displayUrl} alt="biểu đồ" style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', background: C.imgBg }} />
          <button onClick={() => {
            if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
            onClear();
          }} style={{ position: 'absolute', top: 6, right: 6, width: 24, height: 24, borderRadius: 7, border: 'none', background: 'rgba(11,21,33,0.78)', color: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
          {loadingImg && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.2)' }}>
              <RotateCcw size={20} color={C.white} className="jd-spin" style={{ animation: 'jd-keyframes-spin 1s linear infinite' }} />
            </div>
          )}
        </div>
      ) : (
        <button onClick={() => setModalOpen(true)}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, width: 150, height: 90, border: `1.5px dashed ${C.dashedSoft}`, borderRadius: 10, cursor: 'pointer', background: 'var(--jd-surface-3)', fontFamily: 'inherit' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.goldDark} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="3" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>Tải ảnh biểu đồ</span>
        </button>
      )}
      <UploadModal open={modalOpen} onClose={() => setModalOpen(false)} folderId={null} onUploadSuccess={handleSuccess} />
    </>
  );
});

// ==========================================
// SECTIONS COMPONENTS
// ==========================================

interface RiskSectionProps {
  date: string;
  account_number: string;
  journal_data: Journal;
  form_configs: FormFieldConfig[];
  ui_labels: UILabels_DailyPlan['step_1'];
  onReload?: () => void;
  open: boolean;
  can_edit?: boolean;
  onToggle: () => void;
  onSaveSuccess?: () => void;
  tooltips?: PostTradeTooltipData | null;
  sectionRef?: React.MutableRefObject<SectionHandle | null>;
}

function RiskSection({ date, account_number, journal_data, form_configs, ui_labels, onReload, open, onToggle, onSaveSuccess, can_edit, tooltips, sectionRef }: RiskSectionProps) {
  const [loaded, setLoaded] = useState(false);
  const [riskPerTrade, setRiskPerTrade] = useState('');
  const [dailyLoss, setDailyLoss] = useState('');
  const [maxTrades, setMaxTrades] = useState('');
  const [tradeReady, setTradeReady] = useState<string | null>(null);
  const [stopAtTarget, setStopAtTarget] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  const [saveStatus, setSaveStatus] = useSaveStatus();
  const currentLanguage = i18next.language;

  const tradeReadinessConfig = useMemo(() => getFormConfigsField('field_key', 'trade_readiness', form_configs), [form_configs]);
  const stopAtTargetConfig = useMemo(() => getFormConfigsField('field_key', 'stop_at_target', form_configs), [form_configs]);

  const tradeReadinessOptions = tradeReadinessConfig?.options || [];
  const stopAtTargetOptions = stopAtTargetConfig?.options || [];

  const readSession = (key: string): string | null => {
    const raw = sessionStorage.getItem(key);
    if (raw === null) return null;
    try { return JSON.parse(raw); } catch { return raw; }
  };

  const onload = useCallback(() => {
    // Ưu tiên: session > API > ''
    setRiskPerTrade(readSession(`risk_per_trade_${date}_${journal_data.id}_${account_number}_step1`) || journal_data.max_risk_per_trade?.toString() || '');
    setDailyLoss(readSession(`daily_loss_${date}_${journal_data.id}_${account_number}_step1`) || journal_data.max_daily_loss?.toString() || '');
    setMaxTrades(readSession(`max_trades_${date}_${journal_data.id}_${account_number}_step1`) || journal_data.max_trades_per_day?.toString() || '');
    setTradeReady(readSession(`trade_readiness_${date}_${journal_data.id}_${account_number}_step1`) || journal_data.psychology_market_condition || null);
    setStopAtTarget(readSession(`stop_at_target_${date}_${journal_data.id}_${account_number}_step1`) || journal_data.target_action || null);
  }, [journal_data, date, account_number]);

  useEffect(() => {
    onload();
    setLoaded(true);
  }, [onload]);

  const validateAndHighlight = useCallback(() => {
    const isMissing = !riskPerTrade?.trim() ||
      !dailyLoss?.trim() ||
      !maxTrades?.trim() ||
      !tradeReady ||
      !stopAtTarget;
    if (isMissing) {
      setShowErrors(true);
      return false;
    }
    return true;
  }, [riskPerTrade, dailyLoss, maxTrades, tradeReady, stopAtTarget]);

  const save = useCallback(async () => {
    if (!loaded || saveStatus === 'saving') return false;
    if (!validateAndHighlight()) {
      message.warning('Vui lòng điền đầy đủ các thông tin mục tiêu & quản trị rủi ro!');
      return false;
    }
    setSaveStatus('saving');
    try {
      await updateDailyChecklist({
        date,
        daily_plan_data: {
          max_risk_per_trade: parseFloat(riskPerTrade) || undefined,
          max_daily_loss: parseFloat(dailyLoss) || undefined,
          max_trades_allowed: parseInt(maxTrades) || undefined,
          trade_readiness: tradeReady || undefined,
          stop_at_target: stopAtTarget || undefined,
        }
      });
      setSaveStatus('saved');
      setShowErrors(false);
      sessionStorage.removeItem(`risk_per_trade_${date}_${journal_data.id}_${account_number}_step1`);
      sessionStorage.removeItem(`daily_loss_${date}_${journal_data.id}_${account_number}_step1`);
      sessionStorage.removeItem(`max_trades_${date}_${journal_data.id}_${account_number}_step1`);
      sessionStorage.removeItem(`trade_readiness_${date}_${journal_data.id}_${account_number}_step1`);
      sessionStorage.removeItem(`stop_at_target_${date}_${journal_data.id}_${account_number}_step1`);
      onSaveSuccess?.();
      return true;
    } catch {
      setSaveStatus('error');
      return false;
    } finally {
      onReload?.();
    }
  }, [loaded, saveStatus, setSaveStatus, validateAndHighlight, date, riskPerTrade, dailyLoss, maxTrades, tradeReady, stopAtTarget, onSaveSuccess, onReload]);

  const isUsingSessionStore = sessionStorage.getItem(`risk_per_trade_${date}_${journal_data.id}_${account_number}_step1`) !== null || sessionStorage.getItem(`daily_loss_${date}_${journal_data.id}_${account_number}_step1`) !== null || sessionStorage.getItem(`max_trades_${date}_${journal_data.id}_${account_number}_step1`) !== null || sessionStorage.getItem(`trade_readiness_${date}_${journal_data.id}_${account_number}_step1`) !== null || sessionStorage.getItem(`stop_at_target_${date}_${journal_data.id}_${account_number}_step1`) !== null;

  useEffect(() => {
    if (sectionRef) {
      sectionRef.current = {
        isUnsaved: () => {
          return sessionStorage.getItem(`risk_per_trade_${date}_${journal_data.id}_${account_number}_step1`) !== null ||
            sessionStorage.getItem(`daily_loss_${date}_${journal_data.id}_${account_number}_step1`) !== null ||
            sessionStorage.getItem(`max_trades_${date}_${journal_data.id}_${account_number}_step1`) !== null ||
            sessionStorage.getItem(`trade_readiness_${date}_${journal_data.id}_${account_number}_step1`) !== null ||
            sessionStorage.getItem(`stop_at_target_${date}_${journal_data.id}_${account_number}_step1`) !== null;
        },
        save: async () => {
          return await save();
        },
        isIncomplete: () => {
          return !riskPerTrade?.trim() ||
            !dailyLoss?.trim() ||
            !maxTrades?.trim() ||
            !tradeReady ||
            !stopAtTarget;
        },
        validateAndHighlight: () => validateAndHighlight()
      };
    }
    return () => {
      if (sectionRef) sectionRef.current = null;
    };
  }, [sectionRef, date, journal_data.id, account_number, save, validateAndHighlight, riskPerTrade, dailyLoss, maxTrades, tradeReady, stopAtTarget]);

  const reset = useCallback(() => {
    setRiskPerTrade(''); setDailyLoss(''); setMaxTrades(''); setTradeReady(null); setStopAtTarget(null);
    setShowErrors(false);
  }, []);

  const answered = (tradeReady ? 1 : 0) + (stopAtTarget ? 1 : 0);
  const isCompleted = answered === 2;
  const chip = <div style={{ width: 'max-content', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
    <StatusChip tone={isUsingSessionStore ? "gold" : isCompleted ? "green" : "gray"} dot>{isUsingSessionStore ? "Chưa lưu" : isCompleted ? "Đã hoàn thành" : "Chưa hoàn thành"}</StatusChip>
  </div>;

  return (
    <CollapsibleSection num={1} title={"Mục tiêu & Quản trị rủi ro hôm nay"} chip={chip} open={open} onToggle={onToggle}>
      <div className="jd-three-col" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 22 }}>
        <PercentField
          label={ui_labels.risk_limit}
          value={riskPerTrade}
          onChange={(value) => {
            setRiskPerTrade(value);
            sessionStorage.setItem(`risk_per_trade_${date}_${journal_data.id}_${account_number}_step1`, value);
          }}
          placeholder="VD: 1.0"
          suffix="%"
          tooltipKey={TOOLTIP_CONFIG.startOfDay.riskLimit}
          tooltips={tooltips}
          hasError={showErrors && !riskPerTrade?.trim()}
        />
        <PercentField
          label={ui_labels.daily_loss_limit}
          value={dailyLoss}
          onChange={(value) => {
            setDailyLoss(value);
            sessionStorage.setItem(`daily_loss_${date}_${journal_data.id}_${account_number}_step1`, value);
          }}
          placeholder="VD: 3.0"
          suffix="%"
          tooltipKey={TOOLTIP_CONFIG.startOfDay.dailyLossLimit}
          tooltips={tooltips}
          hasError={showErrors && !dailyLoss?.trim()}
        />
        <PercentField
          label={ui_labels.max_trades}
          value={maxTrades}
          onChange={(value) => {
            setMaxTrades(value);
            sessionStorage.setItem(`max_trades_${date}_${journal_data.id}_${account_number}_step1`, value);
          }}
          placeholder="VD: 3"
          tooltipKey={TOOLTIP_CONFIG.startOfDay.maxTrades}
          tooltips={tooltips}
          hasError={showErrors && !maxTrades?.trim()}
        />
      </div>

      <div className="jd-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <DynamicOptionGroup
          label={tradeReadinessConfig?.field_label || ''}
          options={tradeReadinessOptions}
          selectedValue={tradeReady}
          onChange={(value) => {
            setTradeReady(value);
            sessionStorage.setItem(`trade_readiness_${date}_${journal_data.id}_${account_number}_step1`, value);
          }}
          currentLanguage={currentLanguage}
          tooltipKey={TOOLTIP_CONFIG.startOfDay.tradeReadiness}
          tooltips={tooltips}
          hasError={showErrors && !tradeReady}
        />
        <DynamicOptionGroup
          label={stopAtTargetConfig?.field_label || ''}
          options={stopAtTargetOptions}
          selectedValue={stopAtTarget}
          onChange={(value) => {
            setStopAtTarget(value);
            sessionStorage.setItem(`stop_at_target_${date}_${journal_data.id}_${account_number}_step1`, value);
          }}
          currentLanguage={currentLanguage}
          tooltipKey={TOOLTIP_CONFIG.startOfDay.stopAtTarget}
          tooltips={tooltips}
          hasError={showErrors && !stopAtTarget}
        />
      </div>
      {
        can_edit ? (
          <SaveResetFooter saveStatus={saveStatus} onReset={reset} onSave={save} />
        )
          : (
            null
          )

      }
    </CollapsibleSection>
  );
}

interface HealthSectionProps {
  date: string;
  journal_data: Journal;
  account_number: string;
  form_configs: FormFieldConfig[];
  ui_labels: UILabels_DailyPlan['step_2'];
  onReload?: () => void;
  open: boolean;
  can_edit?: boolean;
  onToggle: () => void;
  onSaveSuccess?: () => void;
  tooltips?: PostTradeTooltipData | null;
  sectionRef?: React.MutableRefObject<SectionHandle | null>;
}

export function HealthSection({ date, journal_data, account_number, form_configs, ui_labels, onReload, open, can_edit, onToggle, onSaveSuccess, tooltips, sectionRef }: HealthSectionProps) {
  const [cond, setCond] = useState<Record<string, number>>({});
  const [moods, setMoods] = useState<string[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [saveStatus, setSaveStatus] = useSaveStatus();

  const currentLanguage = i18next.language;

  const onChangeField = useCallback(async (fieldKey: string, value: any) => {
    if (!isLoaded) return;
    try {
      await uploadFieldData_daily_plan_data(fieldKey as any, value, { currentDate: date });
    } catch (error) {
      console.error(`Lỗi khi cập nhật trường ${fieldKey}:`, error);
    }
  }, [isLoaded, date]);

  const healthConditionItems = useMemo(() =>
    form_configs.filter(item => item.phase === 'daily_plan' && item.step === 2 && item.input_type === 'rating'),
    [form_configs]
  );

  const healthConditionTags = useMemo(() =>
    healthConditionItems[0]?.options || ['Tệ', 'Yếu', 'Tạm ổn', 'Tốt', 'Rất tốt'],
    [healthConditionItems]
  );

  const emotionConfig = useMemo(() =>
    form_configs.find(item => item.phase === 'daily_plan' && item.step === 2 && item.field_key === 'current_emotions'),
    [form_configs]
  );

  const moodOptions = useMemo(() => {
    const rawMoodOptions = emotionConfig?.options || [];
    return rawMoodOptions.map((opt: any) => {
      const label = getOptionLabel(opt, currentLanguage);
      const value = getOptionValue(opt);
      const kind = opt.type === 'danger' || opt.type === 'warning' ? 'bad' : 'good';
      return { id: value || label, label, kind };
    });
  }, [emotionConfig, currentLanguage]);

  useEffect(() => {
    if (journal_data) {
      const initialCond: Record<string, number> = {};
      const apiTags = journal_data.physical_health_tags || {};

      healthConditionItems.forEach(item => {
        const apiKey = item.field_key as keyof PhysicalHealthTags;
        const apiValueStr = apiTags[apiKey];

        // Ưu tiên: session > API > 0
        const sessionRaw = sessionStorage.getItem(`health_condition_${item.field_key}_${date}_${journal_data.id}_${account_number}_step1`);
        if (sessionRaw !== null) {
          initialCond[item.field_key] = parseInt(sessionRaw) || 0;
        } else {
          let score = RATING_MAP[apiValueStr] || 0;
          if (score === 0 && apiValueStr) {
            const index = healthConditionTags.findIndex((opt: any) => getOptionValue(opt) === apiValueStr || getOptionLabel(opt, currentLanguage) === apiValueStr);
            if (index !== -1) score = index + 1;
          }
          initialCond[item.field_key] = score;
        }
      });

      setCond(initialCond);

      // Ưu tiên: session > API > []
      const sessionMoods = sessionStorage.getItem(`mood_${date}_${journal_data.id}_${account_number}_step1`);
      if (sessionMoods) {
        try {
          const parsed = JSON.parse(sessionMoods);
          setMoods(Array.isArray(parsed) ? parsed : []);
        } catch { setMoods([]); }
      } else {
        const rawEmotion: any = journal_data.current_emotion;
        let parsedMoods: string[] = [];
        if (Array.isArray(rawEmotion) && rawEmotion.length > 0) {
          parsedMoods = rawEmotion;
        } else if (typeof rawEmotion === 'string' && (rawEmotion as string).trim() !== '') {
          try {
            const parsed = JSON.parse(rawEmotion);
            parsedMoods = Array.isArray(parsed) ? parsed : [rawEmotion];
          } catch {
            parsedMoods = [rawEmotion];
          }
        }
        setMoods(parsedMoods);
      }

      setIsLoaded(true);
    }
  }, [journal_data, healthConditionItems, healthConditionTags, currentLanguage, date, account_number]);

  const handleRatingChange = useCallback((key: string, ratingValue: number) => {
    setCond(prev => ({ ...prev, [key]: ratingValue }));
  }, []);

  const handleMoodToggle = useCallback((moodId: string) => {
    setMoods(prev => {
      const arr = Array.isArray(prev) ? prev : [];
      const next = arr.includes(moodId) ? arr.filter(x => x !== moodId) : [...arr, moodId];
      sessionStorage.setItem(`mood_${date}_${journal_data.id}_${account_number}_step1`, JSON.stringify(next));
      return next;
    });
  }, [date, journal_data.id, account_number]);

  const reset = useCallback(() => {
    const clearedCond: Record<string, number> = {};
    healthConditionItems.forEach(item => {
      clearedCond[item.field_key] = 0;
    });
    setCond(clearedCond);
    setMoods([]);
    setShowErrors(false);
    onChangeField('physical_health_tags', {});
    onChangeField('current_emotion', []);
    sessionStorage.removeItem(`health_condition_sleep_quality_${date}_${journal_data.id}_${account_number}_step1`);
    sessionStorage.removeItem(`health_condition_mental_alertness_${date}_${journal_data.id}_${account_number}_step1`);
    sessionStorage.removeItem(`health_condition_physical_comfort_${date}_${journal_data.id}_${account_number}_step1`);
    sessionStorage.removeItem(`health_condition_no_distractions_${date}_${journal_data.id}_${account_number}_step1`);
    sessionStorage.removeItem(`health_condition_quiet_workspace_${date}_${journal_data.id}_${account_number}_step1`);
    sessionStorage.removeItem(`mood_${date}_${journal_data.id}_${account_number}_step1`);
    onReload?.();
  }, [healthConditionItems, onChangeField, onReload, date, journal_data.id, account_number]);

  const validateAndHighlight = useCallback(() => {
    const missingCond = healthConditionItems.some(({ field_key }) => (cond[field_key] ?? 0) === 0);
    const safeMoods = Array.isArray(moods) ? moods : [];
    const missingMood = safeMoods.length === 0;
    if (missingCond || missingMood) {
      setShowErrors(true);
      return false;
    }
    return true;
  }, [healthConditionItems, cond, moods]);

  const save = useCallback(async () => {
    if (!isLoaded || saveStatus === 'saving') return false;
    if (!validateAndHighlight()) {
      message.warning('Vui lòng hoàn thành đánh giá thể trạng và chọn cảm xúc!');
      return false;
    }
    try {
      setSaveStatus('saving');
      await updateDailyChecklist({
        date,
        daily_plan_data: {
          sleep_quality: cond['sleep_quality'] ? (getTagNameByRating(cond['sleep_quality'], healthConditionTags, currentLanguage) as RatingLevel) : undefined,
          mental_alertness: cond['mental_alertness'] ? (getTagNameByRating(cond['mental_alertness'], healthConditionTags, currentLanguage) as RatingLevel) : undefined,
          physical_comfort: cond['physical_comfort'] ? (getTagNameByRating(cond['physical_comfort'], healthConditionTags, currentLanguage) as RatingLevel) : undefined,
          no_distractions: cond['no_distractions'] ? (getTagNameByRating(cond['no_distractions'], healthConditionTags, currentLanguage) as RatingLevel) : undefined,
          quiet_workspace: cond['quiet_workspace'] ? (getTagNameByRating(cond['quiet_workspace'], healthConditionTags, currentLanguage) as RatingLevel) : undefined,
          current_emotions: moods.length > 0 ? moods : undefined,
        }
      });
      setSaveStatus('saved');
      setShowErrors(false);
      sessionStorage.removeItem(`health_condition_sleep_quality_${date}_${journal_data.id}_${account_number}_step1`);
      sessionStorage.removeItem(`health_condition_mental_alertness_${date}_${journal_data.id}_${account_number}_step1`);
      sessionStorage.removeItem(`health_condition_physical_comfort_${date}_${journal_data.id}_${account_number}_step1`);
      sessionStorage.removeItem(`health_condition_no_distractions_${date}_${journal_data.id}_${account_number}_step1`);
      sessionStorage.removeItem(`health_condition_quiet_workspace_${date}_${journal_data.id}_${account_number}_step1`);
      sessionStorage.removeItem(`mood_${date}_${journal_data.id}_${account_number}_step1`);
      onSaveSuccess?.();
      return true;
    } catch (error) {
      setSaveStatus('error');
      console.error("Lỗi khi lưu checklist:", error);
      return false;
    } finally {
      onReload?.();
    }
  }, [isLoaded, saveStatus, setSaveStatus, validateAndHighlight, date, cond, healthConditionTags, currentLanguage, moods, onSaveSuccess, onReload, journal_data.id, account_number]);

  const isUsingSessionStore = sessionStorage.getItem(`health_condition_sleep_quality_${date}_${journal_data.id}_${account_number}_step1`) !== null ||
    sessionStorage.getItem(`health_condition_mental_alertness_${date}_${journal_data.id}_${account_number}_step1`) !== null ||
    sessionStorage.getItem(`health_condition_physical_comfort_${date}_${journal_data.id}_${account_number}_step1`) !== null ||
    sessionStorage.getItem(`health_condition_no_distractions_${date}_${journal_data.id}_${account_number}_step1`) !== null ||
    sessionStorage.getItem(`health_condition_quiet_workspace_${date}_${journal_data.id}_${account_number}_step1`) !== null ||
    sessionStorage.getItem(`mood_${date}_${journal_data.id}_${account_number}_step1`) !== null;

  const doneCount = healthConditionItems.filter(({ field_key }) => (cond[field_key] ?? 0) > 0).length;
  const total = healthConditionItems.length;
  const isCompleted = doneCount === total && total > 0;

  useEffect(() => {
    if (sectionRef) {
      sectionRef.current = {
        isUnsaved: () => {
          return sessionStorage.getItem(`health_condition_sleep_quality_${date}_${journal_data.id}_${account_number}_step1`) !== null ||
            sessionStorage.getItem(`health_condition_mental_alertness_${date}_${journal_data.id}_${account_number}_step1`) !== null ||
            sessionStorage.getItem(`health_condition_physical_comfort_${date}_${journal_data.id}_${account_number}_step1`) !== null ||
            sessionStorage.getItem(`health_condition_no_distractions_${date}_${journal_data.id}_${account_number}_step1`) !== null ||
            sessionStorage.getItem(`health_condition_quiet_workspace_${date}_${journal_data.id}_${account_number}_step1`) !== null ||
            sessionStorage.getItem(`mood_${date}_${journal_data.id}_${account_number}_step1`) !== null;
        },
        save: async () => {
          return await save();
        },
        isIncomplete: () => {
          const done = healthConditionItems.filter(({ field_key }) => (cond[field_key] ?? 0) > 0).length;
          const totalCount = healthConditionItems.length;
          const safeMoods = Array.isArray(moods) ? moods : [];
          return done < totalCount || totalCount === 0 || safeMoods.length === 0;
        },
        validateAndHighlight: () => validateAndHighlight()
      };
    }
    return () => {
      if (sectionRef) sectionRef.current = null;
    };
  }, [sectionRef, date, journal_data.id, account_number, save, validateAndHighlight, healthConditionItems, cond, moods]);

  const chip = <StatusChip tone={isUsingSessionStore ? "gold" : isCompleted ? "green" : "gray"} dot>{isUsingSessionStore ? "Chưa lưu" : isCompleted ? "Đã hoàn thành" : "Chưa hoàn thành"}</StatusChip>;

  const condVals = healthConditionItems.map(({ field_key }) => cond[field_key]).filter((v): v is number => !!v && v > 0);
  const avg = condVals.length ? condVals.reduce((a, b) => a + b, 0) / condVals.length : 0;

  const safeMoods = Array.isArray(moods) ? moods : [];
  const badMoods = safeMoods.filter(id => moodOptions.find(m => m.id === id && m.kind === 'bad')).length;
  const allCond = doneCount === total;
  const good = allCond && avg >= 3.5 && badMoods === 0;
  const showWarn = !good && (allCond || badMoods > 0);

  return (
    <CollapsibleSection num={2} title={"Kiểm tra thể trạng & Sức khỏe tâm lý"} chip={chip} open={open} onToggle={onToggle}>
      <div className="jd-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28, padding: 0 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: C.faint, marginBottom: 14 }}>
            {ui_labels.work_conditions_title}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {healthConditionItems.map((item) => {
              const key = item.field_key;
              const label = item.field_label;
              const val = cond[key] ?? 0;
              const hasError = showErrors && val === 0;
              const tone = val === 0 ? (hasError ? { bg: '#fee2e2', tx: '#dc2626' } : { bg: C.neutralBg, tx: C.faint }) : val >= 4 ? { bg: C.greenBg, tx: C.greenTx } : val >= 3 ? { bg: C.goldBg, tx: C.goldDark } : { bg: C.redBg, tx: C.redTx };
              const fill = val >= 4 ? C.green : val >= 3 ? C.gold : C.red;
              const itemTt = getTooltipContent(tooltips, `checklist.startofday.${key}`);
              return (
                <div key={key} style={{
                  padding: hasError ? '8px 10px' : '0',
                  borderRadius: 12,
                  border: hasError ? '1.5px solid #ef4444' : '1.5px solid transparent',
                  transition: 'all .2s'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 13.5, fontWeight: 600, color: hasError ? '#ef4444' : C.title }}>{label}</span>
                      {itemTt && (
                        <Tooltip title={itemTt.text} placement={itemTt.placement || "top"}>
                          <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
                        </Tooltip>
                      )}
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, padding: '3px 11px', borderRadius: 999, background: tone.bg, color: tone.tx }}>
                      {val === 0 ? 'Chưa chọn' : getTagNameByRating(val, healthConditionTags, currentLanguage)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 7 }}>
                    {[1, 2, 3, 4, 5].map(n => (
                      <span
                        key={n}
                        onClick={() => {
                          const prev = cond[key] ?? 0;
                          handleRatingChange(key, n);
                          if (prev !== n) {
                            if (n === 0) {
                              sessionStorage.removeItem(`health_condition_${key}_${date}_${journal_data.id}_${account_number}_step1`);
                            } else {
                              sessionStorage.setItem(`health_condition_${key}_${date}_${journal_data.id}_${account_number}_step1`, n.toString());
                            }
                          }
                        }}
                        style={{ flex: 1, height: 10, borderRadius: 99, cursor: 'pointer', transition: 'background .15s', background: n <= val ? fill : (hasError ? '#fecaca' : C.track) }}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: (showErrors && safeMoods.length === 0) ? '#ef4444' : C.title }}>
                {ui_labels.emotion_title}
              </div>
              {getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.currentEmotions) && (
                <Tooltip
                  title={getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.currentEmotions)?.text}
                  placement={getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.currentEmotions)?.placement || "top"}
                >
                  <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
                </Tooltip>
              )}
            </div>
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 9,
              padding: (showErrors && safeMoods.length === 0) ? '8px' : '0',
              borderRadius: 12,
              border: (showErrors && safeMoods.length === 0) ? '1.5px solid #ef4444' : '1.5px solid transparent',
              transition: 'all .2s'
            }}>
              {moodOptions.map(({ id, label, kind }) => {
                const on = safeMoods.includes(id);
                const c = kind === 'good' ? { bd: C.greenBd, bg: C.greenBg, tx: C.greenTx } : { bd: C.redBd, bg: C.redBg, tx: C.redTx };
                return (
                  <span
                    key={id}
                    onClick={() => handleMoodToggle(id)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, padding: '8px 14px', borderRadius: 999, cursor: 'pointer', transition: 'all .15s', border: `1.5px solid ${on ? c.bd : C.chipOffBd}`, background: on ? c.bg : C.surface, color: on ? c.tx : C.chipOffTx }}
                  >
                    {on && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}><path d="M5 12l5 5L20 6" /></svg>}
                    {label}
                  </span>
                );
              })}
            </div>
          </div>
          <HealthVerdict good={good} showWarn={showWarn} allCond={allCond} />
        </div>
      </div>
      {
        can_edit ? (
          <SaveResetFooter saveStatus={saveStatus} onReset={reset} onSave={save} />
        )
          : (
            null
          )
      }
    </CollapsibleSection>
  );
}

interface MarketSectionProps {
  date: string;
  account_number: string;
  symbol: string;
  journal_data: Journal;
  form_configs: any[];
  ui_labels: any;
  onReload?: () => void;
  open: boolean;
  can_edit?: boolean;
  onToggle: () => void;
  onSaveSuccess?: () => void;
  tooltips?: PostTradeTooltipData | null;
  sectionRef?: React.MutableRefObject<SectionHandle | null>;
}

interface UIKeyLevel {
  id: number;
  zone: string;
  type: string;
}

export function MarketSection({
  date, account_number, symbol, journal_data, form_configs, ui_labels, onReload, open, can_edit, onToggle, onSaveSuccess, tooltips, sectionRef
}: MarketSectionProps) {
  const [d1, setD1] = useState('');
  const [h4, setH4] = useState('');
  const [h1m15, setH1m15] = useState('');
  const [scenarioNote, setScenarioNote] = useState('');
  const [dir, setDir] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [keyLevels, setKeyLevels] = useState<UIKeyLevel[]>([{ id: 1, zone: '', type: '' }]);
  const [liqType, setLiqType] = useState<string | null>(null);
  const [clean, setClean] = useState<string | null>(null);
  const [sessions, setSessions] = useState<string | null>(null);
  const [tradeExecutionId, setTradeExecutionId] = useState<number | undefined>(undefined);
  const [showErrors, setShowErrors] = useState(false);

  const [saveStatus, setSaveStatus] = useSaveStatus();
  const currentLanguage = i18next.language;

  const nextId = useRef(2);
  const isLoaded = useRef(false);
  const isMounted = useIsMounted();
  const keyLevelsRef = useRef<UIKeyLevel[]>(keyLevels);

  // Giữ ref đồng bộ với state để dùng trong callbacks
  useEffect(() => { keyLevelsRef.current = keyLevels; }, [keyLevels]);

  const ssKeys = useMemo(() => ({
    d1: `structure_d1_${date}_${journal_data?.id}_${account_number}_step1`,
    h4: `structure_h4_${date}_${journal_data?.id}_${account_number}_step1`,
    h1m15: `alignment_h1_m15_${date}_${journal_data?.id}_${account_number}_step1`,
    legacyH1m15: `structure_h1m15_${date}_${journal_data?.id}_${account_number}_step1`,
    keyLevels: `key_levels_${date}_${journal_data?.id}_${account_number}_step1`,
    liqType: `liquidity_zone_${date}_${journal_data?.id}_${account_number}_step1`,
    scenarioGuess: `scenario_guess_${date}_${journal_data?.id}_${account_number}_step1`,
    marketCond: `market_condition_${date}_${journal_data?.id}_${account_number}_step1`,
    tradingSession: `trading_session_${date}_${journal_data?.id}_${account_number}_step1`,
  }), [date, journal_data?.id, account_number]);

  const clearSession = useCallback(() => {
    Object.values(ssKeys).forEach(k => sessionStorage.removeItem(k));
  }, [ssKeys]);

  const saveKLToSession = useCallback((kls: UIKeyLevel[]) => {
    sessionStorage.setItem(ssKeys.keyLevels, JSON.stringify(kls));
  }, [ssKeys.keyLevels]);

  const keyLevelTypeOptions = useMemo(() =>
    getFormConfigsField('field_key', 'key_level_type', form_configs)?.options || ['Hỗ trợ', 'Kháng cự', 'Vùng Cung (Supply)', 'Vùng Cầu (Demand)'],
    [form_configs]
  );

  const structureOptions = useMemo(() => {
    const raw = getFormConfigsField('field_key', 'structure_d1', form_configs)?.options || ['Tăng (Bullish)', 'Giảm (Bearish)', 'Đi ngang (Sideway)'];
    return raw.map((opt: any) => typeof opt === 'string' ? opt : getOptionValue(opt));
  }, [form_configs]);

  const h1m15ConsensusOptions = useMemo(() => {
    const raw = getFormConfigsField('field_key', 'alignment_h1_m15', form_configs)?.options || ['Đồng thuận Tăng', 'Đồng thuận Giảm', 'Xung đột / Chưa rõ ràng'];
    return raw.map((opt: any) => typeof opt === 'string' ? opt : getOptionValue(opt));
  }, [form_configs]);

  const liqZoneConfig = useMemo(() => getFormConfigsField('field_key', 'liquidity_zone', form_configs), [form_configs]);
  const marketCondConfig = useMemo(() => getFormConfigsField('field_key', 'market_condition', form_configs), [form_configs]);
  const sessionConfig = useMemo(() => getFormConfigsField('field_key', 'trading_session', form_configs), [form_configs]);

  // Helper đọc sessionStorage (tương thích cả lưu chuỗi thuần lẫn JSON)
  const readSession = (key: string): string | null => {
    const raw = sessionStorage.getItem(key);
    if (raw === null) return null;
    try { return JSON.parse(raw); } catch { return raw; }
  };

  useEffect(() => {
    if (journal_data) {
      isLoaded.current = false;
      const tc = journal_data.technical_context;

      // --- Cấu trúc thị trường --- ưu tiên: session > API > ''
      const sessionD1 = readSession(ssKeys.d1);
      const apiD1 = mapTCApiToUi(tc?.structure_d1 || (journal_data as any).structure_d1, structureOptions);
      setD1(sessionD1 || apiD1 || '');

      const sessionH4 = readSession(ssKeys.h4);
      const apiH4 = mapTCApiToUi(tc?.structure_h4 || (journal_data as any).structure_h4, structureOptions);
      setH4(sessionH4 || apiH4 || '');

      const sessionH1M15 = readSession(ssKeys.h1m15) || readSession(ssKeys.legacyH1m15);
      const apiH1M15 = mapTCApiToUi(tc?.alignment_h1_m15 || (journal_data as any).alignment_h1_m15, h1m15ConsensusOptions);
      setH1m15(sessionH1M15 || apiH1M15 || '');

      // --- Scenario note ---
      setScenarioNote(readSession(ssKeys.scenarioGuess) || journal_data.scenario_guess || '');

      // --- Bias ---
      setDir(journal_data.main_bias ? journal_data.main_bias.toLowerCase() : null);
      setReason(journal_data.bias_reason || '');

      // --- Key Levels --- ưu tiên: session > API
      const sessionKL = sessionStorage.getItem(ssKeys.keyLevels);
      let klLoaded = false;

      if (sessionKL) {
        try {
          const parsed: UIKeyLevel[] = JSON.parse(sessionKL);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setKeyLevels(parsed);
            nextId.current = Math.max(...parsed.map(k => k.id), 0) + 1;
            klLoaded = true;
          }
        } catch { /* ignore */ }
      }

      if (!klLoaded) {
        const apiKeyLevels = journal_data.key_level_zone || (journal_data as any).key_levels;
        if (Array.isArray(apiKeyLevels) && apiKeyLevels.length > 0) {
          const mappedKL = apiKeyLevels.map((kl, index) => ({
            id: index + 1,
            zone: kl.price_zone || '',
            type: kl.zone_type || ''
          }));
          setKeyLevels(mappedKL);
          nextId.current = mappedKL.length + 1;
          klLoaded = true;
        } else if (typeof apiKeyLevels === 'string' && (apiKeyLevels as string).trim() !== '') {
          try {
            const parsed = JSON.parse(apiKeyLevels);
            if (Array.isArray(parsed) && parsed.length > 0) {
              const mappedKL = parsed.map((kl: any, index: number) => ({
                id: index + 1,
                zone: kl.price_zone || '',
                type: kl.zone_type || ''
              }));
              setKeyLevels(mappedKL);
              nextId.current = mappedKL.length + 1;
              klLoaded = true;
            }
          } catch { /* ignore */ }
        }
      }

      if (!klLoaded) {
        setKeyLevels([{ id: 1, zone: '', type: '' }]);
      }

      // --- Liquidity / Market condition / Session --- ưu tiên: session > API > null
      setLiqType(readSession(ssKeys.liqType) || journal_data.liquidity_status || null);
      setClean(readSession(ssKeys.marketCond) || journal_data.market_cleanliness || null);
      setSessions(
        readSession(ssKeys.tradingSession) ||
        (journal_data.trading_session ? journal_data.trading_session.trim() : null) ||
        null
      );

      // --- Trade execution ---
      const matchingExecutions = journal_data?.trade_executions?.filter(e => e.symbol === symbol && e.account_number === account_number) || [];
      const executionWithPreTrade = matchingExecutions.find(e => {
        const p = e.pre_trade_data;
        if (!p) return false;
        return !!(p.key_levels?.length || p.pre_main_bias || p.bias_reason || p.pre_liquidity_zone || p.scenario_guess || p.pre_market_condition || p.pre_trading_session);
      });
      const matchingExecution = executionWithPreTrade || matchingExecutions[0];
      setTradeExecutionId(matchingExecution ? matchingExecution.id : undefined);

      const timer = setTimeout(() => { isLoaded.current = true; }, 50);
      return () => clearTimeout(timer);
    }
  }, [journal_data, structureOptions, h1m15ConsensusOptions, ssKeys, symbol, account_number]);

  const updateKLZone = useCallback((id: number, zoneValue: string) => {
    setKeyLevels(ks => {
      const next = ks.map(k => k.id === id ? { ...k, zone: zoneValue } : k);
      saveKLToSession(next);
      return next;
    });
  }, [saveKLToSession]);

  const updateKLType = useCallback((id: number, typeValue: string) => {
    setKeyLevels(ks => {
      const next = ks.map(k => k.id === id ? { ...k, type: typeValue } : k);
      saveKLToSession(next);
      return next;
    });
  }, [saveKLToSession]);

  const removeKL = useCallback((id: number) => {
    setKeyLevels(ks => {
      const next = ks.filter(k => k.id !== id);
      saveKLToSession(next);
      return next;
    });
  }, [saveKLToSession]);

  const addKL = useCallback(() => {
    const id = nextId.current++;
    setKeyLevels(ks => {
      const next = [...ks, { id, zone: '', type: '' }];
      saveKLToSession(next);
      return next;
    });
  }, [saveKLToSession]);

  const reset = useCallback(() => {
    setD1(''); setH4(''); setH1m15(''); setScenarioNote('');
    setKeyLevels([{ id: 1, zone: '', type: '' }]);
    setLiqType(null); setClean(null); setSessions(null);
    setShowErrors(false);
    clearSession();
  }, [clearSession]);

  const validateAndHighlight = useCallback(() => {
    const kFilled = keyLevels.some(k => k.zone.trim() || k.type.trim());
    const tFilled = !!(d1 && h4 && h1m15);
    const sc = [tFilled, kFilled, liqType, scenarioNote.trim(), clean, !!sessions].filter(Boolean).length;
    if (sc < 6) {
      setShowErrors(true);
      return false;
    }
    return true;
  }, [keyLevels, d1, h4, h1m15, liqType, scenarioNote, clean, sessions]);

  const save = useCallback(async () => {
    if (!isLoaded.current || saveStatus === 'saving') return false;
    if (!validateAndHighlight()) {
      message.warning('Vui lòng điền đầy đủ các thông tin bối cảnh thị trường!');
      return false;
    }
    setSaveStatus('saving');

    const apiD1 = mapTCUiToApi(d1);
    const apiH4 = mapTCUiToApi(h4);
    const apiH1M15 = mapTCUiToApi(h1m15);

    const validKeyLevels = keyLevels
      .filter(k => k.zone.trim() || k.type.trim())
      .map(k => ({
        price_zone: k.zone.trim() || undefined,
        zone_type: k.type.trim() || undefined,
      }));

    const dailyPlanData: DailyPlanData = {
      structure_d1: apiD1,
      structure_h4: apiH4,
      alignment_h1_m15: apiH1M15,
      technical_context: {
        structure_d1: apiD1,
        structure_h4: apiH4,
        alignment_h1_m15: apiH1M15,
      },
      scenario_guess: scenarioNote.trim() || undefined,
      key_levels: validKeyLevels,
      liquidity_zone: liqType || undefined,
      market_condition: clean || undefined,
      trading_session: sessions || undefined,
    };

    const payloadPreTradeData = {
      pre_main_bias: dir === 'buy' ? 'BULLISH' : dir === 'sell' ? 'BEARISH' : 'NEUTRAL',
      bias_reason: reason || undefined,
      key_levels: validKeyLevels,
      pre_liquidity_zone: liqType || undefined,
      scenario_guess: scenarioNote.trim() || undefined,
      pre_market_condition: clean || undefined,
      pre_trading_session: sessions || undefined,
    };

    try {
      const updatePayload: UpdateDailyJournalPayload = { date, daily_plan_data: dailyPlanData };
      await updateDailyChecklist(updatePayload);

      // Chỉ gọi API checklist/trade khi chưa có trade_execution hoặc dữ liệu pre_trade_data chưa có
      const matchingExecutions = journal_data?.trade_executions?.filter(e => e.symbol === symbol && e.account_number === account_number) || [];
      const existingExec = matchingExecutions.find(e => e.id === tradeExecutionId) || matchingExecutions[0];
      const hasPreTradeData = existingExec?.pre_trade_data && (
        (existingExec.pre_trade_data.key_levels && existingExec.pre_trade_data.key_levels.length > 0) ||
        existingExec.pre_trade_data.pre_main_bias ||
        existingExec.pre_trade_data.bias_reason ||
        existingExec.pre_trade_data.pre_liquidity_zone ||
        existingExec.pre_trade_data.scenario_guess ||
        existingExec.pre_trade_data.pre_market_condition ||
        existingExec.pre_trade_data.pre_trading_session
      );

      if (!hasPreTradeData) {
        try {
          const response = await fetch(`${API_URL}/api/v1/be/checklist/trade`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify({
              ...(tradeExecutionId && { id: tradeExecutionId }),
              date,
              journal_id: journal_data.id,
              account_number: account_number.replace("#", ""),
              symbol: symbol,
              status: "draft",
              pre_trade_data: payloadPreTradeData
            }),
          });
          if (!response.ok) {
            console.warn("[MarketSection] Sync draft pre-trade data non-ok response:", response.status);
          }
        } catch (err) {
          console.warn("[MarketSection] Sync draft pre-trade data error:", err);
        }
      }

      if (isMounted.current) {
        setSaveStatus('saved');
        setShowErrors(false);
        clearSession();
        onSaveSuccess?.();
      }
      return true;
    } catch {
      if (isMounted.current) setSaveStatus('error');
      return false;
    } finally {
      if (isMounted.current) {
        onReload?.();
      }
    }
  }, [isLoaded, saveStatus, setSaveStatus, validateAndHighlight, d1, h4, h1m15, keyLevels, scenarioNote, liqType, clean, sessions, dir, reason, date, journal_data?.id, account_number, symbol, isMounted, onSaveSuccess, onReload, clearSession]);

  const isUsingSessionStore = [
    ssKeys.d1,
    ssKeys.h4,
    ssKeys.h1m15,
    ssKeys.legacyH1m15,
    ssKeys.keyLevels,
    ssKeys.liqType,
    ssKeys.scenarioGuess,
    ssKeys.marketCond,
    ssKeys.tradingSession,
  ].some(k => sessionStorage.getItem(k) !== null);

  const keyFilled = keyLevels.some(k => k.zone.trim() || k.type.trim());
  const techFilled = !!(d1 && h4 && h1m15);
  const score = [techFilled, keyFilled, liqType, scenarioNote.trim(), clean, !!sessions].filter(Boolean).length;
  const isCompleted = score >= 6;

  useEffect(() => {
    if (sectionRef) {
      sectionRef.current = {
        isUnsaved: () => {
          return [
            ssKeys.d1,
            ssKeys.h4,
            ssKeys.h1m15,
            ssKeys.legacyH1m15,
            ssKeys.keyLevels,
            ssKeys.liqType,
            ssKeys.scenarioGuess,
            ssKeys.marketCond,
            ssKeys.tradingSession,
          ].some(k => sessionStorage.getItem(k) !== null);
        },
        save: async () => {
          return await save();
        },
        isIncomplete: () => {
          const kFilled = keyLevels.some(k => k.zone.trim() || k.type.trim());
          const tFilled = !!(d1 && h4 && h1m15);
          const sc = [tFilled, kFilled, liqType, scenarioNote.trim(), clean, !!sessions].filter(Boolean).length;
          return sc < 6;
        },
        validateAndHighlight: () => validateAndHighlight()
      };
    }
    return () => {
      if (sectionRef) sectionRef.current = null;
    };
  }, [sectionRef, ssKeys, save, validateAndHighlight, keyLevels, d1, h4, h1m15, liqType, scenarioNote, clean, sessions]);

  const chip = <StatusChip tone={isUsingSessionStore ? "gold" : isCompleted ? "green" : "gray"} dot>{isUsingSessionStore ? "Chưa lưu" : isCompleted ? "Đã hoàn thành" : "Chưa hoàn thành"}</StatusChip>;

  return (
    <CollapsibleSection num={3} title={"Bối cảnh thị trường"} chip={chip} open={open} onToggle={onToggle}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.title, marginBottom: 12 }}>Bối cảnh kỹ thuật thị trường</div>
      <div className="jd-three-col" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
        <MarketSelect label="Cấu trúc D1" value={d1} onChange={
          (value => {
            setD1(value);
            sessionStorage.setItem(ssKeys.d1, JSON.stringify(value));
          })
        } options={structureOptions} currentLanguage={currentLanguage} tooltipKey={TOOLTIP_CONFIG.startOfDay.structureD1} tooltips={tooltips} hasError={showErrors && !d1} />
        <MarketSelect label="Cấu trúc H4" value={h4} onChange={
          (value => {
            setH4(value);
            sessionStorage.setItem(ssKeys.h4, JSON.stringify(value));
          })
        } options={structureOptions} currentLanguage={currentLanguage} tooltipKey={TOOLTIP_CONFIG.startOfDay.structureH4} tooltips={tooltips} hasError={showErrors && !h4} />
        <MarketSelect label="Đồng thuận H1/M15" value={h1m15} onChange={
          (value => {
            setH1m15(value);
            sessionStorage.setItem(ssKeys.h1m15, JSON.stringify(value));
          })
        } options={h1m15ConsensusOptions} currentLanguage={currentLanguage} tooltipKey={TOOLTIP_CONFIG.startOfDay.alignmentH1M15} tooltips={tooltips} hasError={showErrors && !h1m15} />
      </div>

      <Divider />

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 13 }}>
        <div style={{ fontSize: 14, color: (showErrors && !keyLevels.some(k => k.zone.trim() || k.type.trim())) ? '#ef4444' : C.text }}>
          Giá đang tại <b style={{ fontWeight: 700 }}>Key Level quan trọng</b>
        </div>
        {getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.keyLevels) && (
          <Tooltip
            title={getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.keyLevels)?.text}
            placement={getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.keyLevels)?.placement || "top"}
          >
            <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
          </Tooltip>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 18 }}>
        {keyLevels.map(k => (
          <div key={k.id} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <TextInput
              value={k.zone}
              onChange={e => updateKLZone(k.id, e.target.value)}
              placeholder="Vùng giá: 2330 – 2335"
              style={{
                flex: 1,
                ...(showErrors && !k.zone.trim() && { border: '1.5px solid #ef4444', boxShadow: '0 0 0 1px #ef4444' })
              }}
            />

            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flex: 1 }}>
              <select
                value={k.type}
                onChange={e => updateKLType(k.id, e.target.value)}
                style={{
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  width: '100%',
                  padding: '12px 38px 12px 13px',
                  border: (showErrors && !k.type.trim()) ? '1.5px solid #ef4444' : `1px solid ${C.border}`,
                  borderRadius: 11,
                  fontSize: 14,
                  color: k.type ? C.text : C.placeholder,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  outline: 'none',
                  background: C.surface
                }}
              >
                <option value="">{currentLanguage === 'en' ? '-- Select Type --' : '-- Chọn loại --'}</option>
                {keyLevelTypeOptions.map((opt: any, idx: number) => {
                  const val = getOptionValue(opt);
                  const lbl = getOptionLabel(opt, currentLanguage);
                  return <option key={val || idx} value={val}>{lbl}</option>;
                })}
              </select>
              <svg style={{ position: 'absolute', right: 13, pointerEvents: 'none' }} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>

            {keyLevels.length > 1 && (
              <button
                onClick={() => removeKL(k.id)}
                style={{
                  width: 42, height: 44, flex: 'none', borderRadius: 10,
                  border: `1px solid ${C.iconBtnBd}`, background: C.surface,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: C.faint
                }}
              >
                <Trash2 size={15} />
              </button>
            )}
          </div>
        ))}

        <button
          onClick={addKL}
          style={{
            alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '9px 14px', border: `1.5px dashed ${C.addDashedBd}`, borderRadius: 10,
            background: 'var(--jd-surface-2)', color: C.goldDark, fontFamily: 'inherit',
            fontSize: 13, fontWeight: 700, cursor: 'pointer'
          }}
        >
          <Plus size={15} /> Thêm mức giá
        </button>
      </div>

      <Divider style={{ margin: '0 0 18px' }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <div style={{ fontSize: 14, color: (showErrors && !liqType) ? '#ef4444' : C.text }}>
          {liqZoneConfig?.field_label || "Trạng thái thanh khoản"}
        </div>
        {getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.liquidityZone) && (
          <Tooltip
            title={getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.liquidityZone)?.text}
            placement={getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.liquidityZone)?.placement || "top"}
          >
            <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
          </Tooltip>
        )}
      </div>
      <div style={{
        display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap',
        padding: showErrors && !liqType ? '6px' : '0',
        borderRadius: 12,
        border: showErrors && !liqType ? '1.5px solid #ef4444' : '1.5px solid transparent',
        transition: 'all .2s'
      }}>
        {(liqZoneConfig?.options || []).map((option: any) => {
          const val = getOptionValue(option);
          const lbl = getOptionLabel(option, currentLanguage);
          return (
            <Pill key={val} active={liqType === val} tone="neutral" onClick={() => {
              setLiqType(val);
              sessionStorage.setItem(ssKeys.liqType, JSON.stringify(val));
            }}>
              {lbl}
            </Pill>
          );
        })}
      </div>

      <Divider style={{ margin: '0 0 18px' }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 9 }}>
        <div style={{ fontSize: 14, color: (showErrors && !scenarioNote.trim()) ? '#ef4444' : C.text }}>
          Hiểu <b style={{ fontWeight: 700 }}>kịch bản có thể xảy ra tiếp theo</b>
        </div>
        {getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.scenarioGuess) && (
          <Tooltip
            title={getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.scenarioGuess)?.text}
            placement={getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.scenarioGuess)?.placement || "top"}
          >
            <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
          </Tooltip>
        )}
      </div>

      <TextArea
        value={scenarioNote}
        onChange={e => {
          setScenarioNote(e.target.value);
          sessionStorage.setItem(ssKeys.scenarioGuess, JSON.stringify(e.target.value));
        }}
        placeholder="Kịch bản A: giá hồi về Key Level rồi tăng tiếp. Kịch bản B: phá vùng → đảo chiều…"
        style={{
          marginBottom: 18,
          ...(showErrors && !scenarioNote.trim() && { border: '1.5px solid #ef4444', boxShadow: '0 0 0 1px #ef4444' })
        }}
      />

      <Divider style={{ margin: '0 0 18px' }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 9 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: (showErrors && !clean) ? '#ef4444' : C.title }}>
          {marketCondConfig?.field_label || "Độ sạch thị trường"}
        </div>
        {getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.marketCondition) && (
          <Tooltip
            title={getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.marketCondition)?.text}
            placement={getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.marketCondition)?.placement || "top"}
          >
            <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
          </Tooltip>
        )}
      </div>
      <div style={{
        display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap',
        padding: showErrors && !clean ? '6px' : '0',
        borderRadius: 12,
        border: showErrors && !clean ? '1.5px solid #ef4444' : '1.5px solid transparent',
        transition: 'all .2s'
      }}>
        {(marketCondConfig?.options || []).map((option: any) => {
          const val = getOptionValue(option);
          const lbl = getOptionLabel(option, currentLanguage);
          return (
            <Pill key={val} active={clean === val} tone="neutral" onClick={() => {
              setClean(val);
              sessionStorage.setItem(ssKeys.marketCond, JSON.stringify(val));
            }}>
              {lbl}
            </Pill>
          );
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 9 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: (showErrors && !sessions) ? '#ef4444' : C.title }}>
          {sessionConfig?.field_label || "Phiên giao dịch chủ đạo"}
        </div>
        {getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.tradingSession) && (
          <Tooltip
            title={getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.tradingSession)?.text}
            placement={getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.tradingSession)?.placement || "top"}
          >
            <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
          </Tooltip>
        )}
      </div>
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 10,
        padding: showErrors && !sessions ? '6px' : '0',
        borderRadius: 12,
        border: showErrors && !sessions ? '1.5px solid #ef4444' : '1.5px solid transparent',
        transition: 'all .2s'
      }}>
        {(sessionConfig?.options || []).map((opt: any) => {
          const val = getOptionValue(opt);
          const lbl = getOptionLabel(opt, currentLanguage);
          return (
            <Pill key={val} active={sessions === val} tone="neutral" onClick={() => {
              const nextVal = sessions === val ? null : val;
              setSessions(nextVal);
              if (nextVal) {
                sessionStorage.setItem(ssKeys.tradingSession, JSON.stringify(nextVal));
              } else {
                sessionStorage.removeItem(ssKeys.tradingSession);
              }
            }}>
              {lbl}
            </Pill>
          );
        })}
      </div>
      {
        can_edit ? (
          <SaveResetFooter saveStatus={saveStatus} onReset={reset} onSave={save} />
        )
          : (
            null
          )
      }
    </CollapsibleSection>
  );
}

interface NewsSectionProps {
  date: string;
  journal_data: Journal;
  form_configs: FormFieldConfig[];
  ui_labels: UILabels_DailyPlan['step_4'];
  open: boolean;
  can_edit?: boolean;
  onToggle: () => void;
  onSaveSuccess?: () => void;
  onReload?: () => void;
  tooltips?: PostTradeTooltipData | null;
}

function NewsSection({ date, journal_data, open, can_edit, onToggle, onSaveSuccess, onReload, tooltips }: NewsSectionProps) {
  const [actions, setActions] = useState<Record<number, string>>({});
  const [timeFilter, setTimeFilter] = useState<'today' | 'this_week'>('today');
  const [ecoCalendar, setEcoCalendar] = useState<EcoCalendarEvent[] | null>(null);

  const [saveStatus, setSaveStatus] = useSaveStatus();
  const safeCalendar = useMemo(() => ecoCalendar || [], [ecoCalendar]);
  const highCount = useMemo(() => safeCalendar.filter(n => (n.impact || '').toLowerCase() === 'high').length, [safeCalendar]);

  const reset = useCallback(() => { setActions({}); }, []);

  const getVnLevel = (level: string, isRestrict?: boolean) => {
    if (isRestrict) return 'Cấm giao dịch';
    const l = level.toLowerCase();
    if (l === 'high') return 'Cao';
    if (l === 'medium') return 'Trung bình';
    return 'Thấp';
  };

  const fetchEconomicCalendar = useCallback(async () => {
    try {
      if (!date) return;
      let fromDate = date;
      const targetDate = new Date(date);

      if (timeFilter === 'this_week') {
        const dayOfWeek = targetDate.getDay();
        const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const startOfWeek = new Date(targetDate);
        startOfWeek.setDate(targetDate.getDate() - diffToMonday);

        const startYear = startOfWeek.getFullYear();
        const startMonth = String(startOfWeek.getMonth() + 1).padStart(2, '0');
        const startDay = String(startOfWeek.getDate()).padStart(2, '0');
        fromDate = `${startYear}-${startMonth}-${startDay}`;
      }

      const data = await getEcoCalendar(fromDate, date, 'ftmo');
      setEcoCalendar(data || null);
    } catch (error) {
      console.error(error);
      setEcoCalendar([]);
    }
  }, [date, timeFilter]);

  useEffect(() => {
    fetchEconomicCalendar();
  }, [fetchEconomicCalendar]);

  useEffect(() => {
    if (ecoCalendar) {
      const initialActions: Record<number, string> = {};
      safeCalendar.forEach((apiEvent: any, index) => {
        const apiEventName = apiEvent.title || '';
        const savedNewsList = journal_data?.news_snapshot ? JSON.parse(journal_data.news_snapshot) : (journal_data as any)?.news_events;
        if (savedNewsList) {
          const savedEvent = savedNewsList.find(
            (saved: any) => saved.name === apiEventName
          );
          if (savedEvent) {
            const savedAction = savedEvent.action_plan ?? savedEvent.action;
            if (savedAction !== undefined) {
              initialActions[index] = savedAction;
            }
          }
        }
      });
      setActions(initialActions);
    }
  }, [journal_data, ecoCalendar, safeCalendar]);

  const save = useCallback(async () => {
    try {
      setSaveStatus('saving');
      const existingNewsEvents = journal_data?.news_snapshot ? JSON.parse(journal_data.news_snapshot) : (journal_data as any)?.news_events || [];
      const newsMap = new Map();
      existingNewsEvents.forEach((item: any) => {
        if (item && item.name) {
          newsMap.set(item.name, { ...item });
        }
      });

      safeCalendar.forEach((event: any, index) => {
        const eventName = event.title || 'Tin không xác định';
        const eventTimeUtc = formatUtcTime(event.time || '');
        const eventLevel = getVnLevel(event.impact || 'low', event.is_restrict);
        const currentAction = actions[index] || '';

        if (newsMap.has(eventName)) {
          const existing = newsMap.get(eventName);
          newsMap.set(eventName, {
            ...existing,
            name: eventName,
            time_utc: eventTimeUtc,
            level: eventLevel,
            action_plan: currentAction !== '' ? currentAction : (existing.action_plan || existing.action || ''),
            saved_date: existing.saved_date || date
          });
        } else {
          newsMap.set(eventName, {
            name: eventName,
            time_utc: eventTimeUtc,
            level: eventLevel,
            action_plan: currentAction,
            saved_date: date
          });
        }
      });

      const newsEventsToSave = Array.from(newsMap.values());
      const payload = {
        date,
        daily_plan_data: { news_events: newsEventsToSave }
      };

      const response = await updateDailyChecklist(payload);
      if (response && response.success !== false) {
        setSaveStatus('saved');
        onSaveSuccess?.();
      } else {
        setSaveStatus('error');
      }
    } catch {
      setSaveStatus('error');
    } finally {
      onReload?.();
    }
  }, [setSaveStatus, journal_data, safeCalendar, actions, date, onSaveSuccess, onReload]);

  return (
    <CollapsibleSection num={4} title="Lịch tin kinh tế quan trọng" open={open} onToggle={onToggle}
      chip={<StatusChip tone="red" dot>{`${safeCalendar.length} tin · ${highCount} mức cao`}</StatusChip>}>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        {[
          { key: 'today', label: 'Hôm nay' },
          { key: 'this_week', label: 'Tuần này' }
        ].map((item) => {
          const isActive = timeFilter === item.key;
          return (
            <button
              key={item.key}
              onClick={() => setTimeFilter(item.key as 'today' | 'this_week')}
              style={{
                padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: 'none', background: isActive ? '#222' : '#f0f2f5', color: isActive ? '#fff' : '#555', transition: 'all 0.2s'
              }}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      <div className="jd-news-table" style={{ border: `1px solid ${C.borderSoft}`, borderRadius: 12, overflow: 'hidden' }}>
        <div className="jd-news-head" style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1.2fr 1fr', background: 'var(--jd-surface-3)', borderBottom: `1px solid ${C.divider}` }}>
          {['Tên tin tức', 'Thời gian (UTC)', 'Thời gian (Local)', 'Mức độ'].map(h => (
            <div key={h} style={{ padding: '11px 16px', fontSize: 13, fontWeight: 700, letterSpacing: .5, textTransform: 'uppercase', color: C.faint }}>{h}</div>
          ))}
        </div>

        {!ecoCalendar ? (
          <div style={{ padding: '20px', textAlign: 'center', fontSize: 13, color: C.faint }}>Đang tải dữ liệu lịch kinh tế...</div>
        ) : safeCalendar.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', fontSize: 13, color: C.faint }}>Không có tin tức kinh tế quan trọng trong khoảng thời gian này.</div>
        ) : (
          safeCalendar.map((n: any, i) => {
            const isRestrict = n.is_restrict === true;
            const rawLevel = (n.impact || 'low').toLowerCase();
            const m = LEVEL_META[rawLevel] || LEVEL_META['low'];
            const eventName = n.title || 'Tin không xác định';
            const timeOriginal = n.time || '';

            return (
              <div key={i} className="jd-news-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1.2fr 1fr', alignItems: 'center', borderBottom: i < safeCalendar.length - 1 ? `1px solid ${C.rowDivider}` : 'none' }}>
                <div style={{ padding: '13px 16px', fontSize: 13.5, fontWeight: 600, color: C.text }}>{eventName}</div>
                <div style={{ padding: '13px 14px', fontSize: 13, fontWeight: 600, color: C.labelStrong }}>{formatUtcTime(timeOriginal)}</div>
                <div style={{ padding: '13px 14px', fontSize: 13, fontWeight: 600, color: C.muted }}>{formatLocalTime(timeOriginal)}</div>
                <div style={{ padding: '13px 14px' }}>
                  {isRestrict ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, padding: '4px 11px', borderRadius: 999, background: '#fee2e2', border: '1px solid #f87171', color: '#991b1b' }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#dc2626' }} />Cấm giao dịch
                    </span>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, padding: '4px 11px', borderRadius: 999, background: m?.bg, border: `1px solid ${m?.bd}`, color: m?.tx }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: m?.dot }} />{m?.t}
                    </span>
                  )}
                </div>

                {/* <div className="jd-news-action" style={{ padding: '9px 12px' }}>
                  <input
                    value={actions[i] ?? ''}
                    onChange={e => setActions(a => ({ ...a, [i]: e.target.value }))}
                    placeholder="Nhập phương án xử lý..."
                    style={{ width: '100%', padding: '9px 11px', border: `1px solid ${C.border}`, borderRadius: 9, fontSize: 13, color: C.text, outline: 'none', fontFamily: 'inherit', background: C.surface }}
                    onFocus={e => { e.currentTarget.style.borderColor = C.gold; }}
                    onBlur={e => { e.currentTarget.style.borderColor = C.border; }}
                  />
                </div> */}
              </div>
            );
          })
        )}
      </div>
      {/* {
        can_edit ? (
          <SaveResetFooter saveStatus={saveStatus} onReset={reset} onSave={save} />
        )
          : (
            null
          )
      } */}
    </CollapsibleSection>
  );
}

interface SetupSectionProps {
  date: string;
  num: number;
  side: 'buy' | 'sell';
  account_number: string;
  journal_data: Journal;
  form_configs: FormFieldConfig[];
  ui_labels: UILabels_DailyPlan['step_5' | 'step_6'];
  title: string;
  onReload?: () => void;
  open: boolean;
  can_edit?: boolean;
  onToggle: () => void;
  onSaveSuccess?: () => void;
  tooltips?: PostTradeTooltipData | null;
  sectionRef?: React.MutableRefObject<SectionHandle | null>;
}

function SetupSection({ date, num, side, account_number, journal_data, form_configs, ui_labels, title, onReload, open, can_edit, onToggle, onSaveSuccess, tooltips, sectionRef }: SetupSectionProps) {
  const [id, setId] = useState<number | undefined>(undefined);
  const currentLanguage = i18next.language;

  const [price, setPrice] = useState('');
  const [band, setBand] = useState('');
  const [rr, setRr] = useState<string | null>(null);
  const [sl, setSl] = useState('');
  const [tp, setTp] = useState('');
  const [trigger, setTrigger] = useState('');
  const [cancel, setCancel] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  const [customRROptions, setCustomRROptions] = useState<string[]>([]);
  const [isAddingRR, setIsAddingRR] = useState(false);
  const [customRRValue, setCustomRRValue] = useState('');

  const [saveStatus, setSaveStatus] = useSaveStatus(1500);

  const isBuy = side === 'buy';
  const targetType = isBuy ? 'BUY' : 'SELL';
  const isLoaded = useRef(false);

  const rrRatioConfig = useMemo(() =>
    getFormConfigsField('field_key', isBuy ? 'buy_rr_ratio' : 'sell_rr_ratio', form_configs),
    [isBuy, form_configs]
  );

  const rrOptionsFromConfig = useMemo(() => rrRatioConfig?.options || [
    { label_en: '1:1', label_vi: '1:1', value: '1' },
    { label_en: '1:2', label_vi: '1:2', value: '2' },
    { label_en: '1:3', label_vi: '1:3', value: '3' },
    { label_en: '1:4', label_vi: '1:4', value: '4' },
    { label_en: '1:5', label_vi: '1:5', value: '5' },
  ], [rrRatioConfig]);

  const jId = journal_data?.id ?? '';
  const ssKey = useCallback((field: string) => `${isBuy ? 'buy' : 'sell'}_${field}_${date}_${jId}_${account_number}_step1`, [isBuy, date, jId, account_number]);

  // Helper đọc sessionStorage cho SetupSection
  const readSetupSession = useCallback((field: string): string | null => {
    const raw = sessionStorage.getItem(ssKey(field)) ?? sessionStorage.getItem(`${isBuy ? 'buy' : 'sell'}_${field}_${date}_undefined_${account_number}_step1`);
    if (raw === null) return null;
    try {
      const parsed = JSON.parse(raw);
      // Chỉ dùng giá trị parse nếu là string, tránh trường hợp JSON.parse trả về number/null/object
      return typeof parsed === 'string' ? parsed : raw;
    } catch { return raw; }
  }, [ssKey, isBuy, date, account_number]);

  const removeSetupSession = useCallback(() => {
    const fields = ['price_base', 'price_margin', 'rr', 'sl', 'tp', 'trigger', 'cancel'];
    fields.forEach(f => {
      sessionStorage.removeItem(ssKey(f));
      sessionStorage.removeItem(`${isBuy ? 'buy' : 'sell'}_${f}_${date}_undefined_${account_number}_step1`);
    });
  }, [ssKey, isBuy, date, account_number]);

  const parseRRNumber = useCallback((rrStr: string | null | undefined): number | null => {
    if (!rrStr) return null;
    const clean = rrStr.trim();
    if (clean.includes(':')) {
      const parts = clean.split(':');
      const num = parseFloat(parts[0]);
      const den = parseFloat(parts[1]);
      if (!isNaN(num) && !isNaN(den) && num > 0 && den > 0) {
        return den / num;
      }
    }
    const n = parseFloat(clean);
    return !isNaN(n) && n > 0 ? n : null;
  }, []);

  const getPrecision = (...nums: (string | number)[]) => {
    let maxDec = 1;
    for (const n of nums) {
      if (!n) continue;
      const s = String(n).trim();
      if (s.includes('.')) {
        const dec = s.split('.')[1].length;
        if (dec > maxDec) maxDec = dec;
      }
    }
    return Math.min(Math.max(maxDec, 1), 4);
  };

  const formatPrice = (val: number, precision: number = 2): string => {
    if (isNaN(val)) return '';
    const fixed = val.toFixed(precision);
    return parseFloat(fixed).toString();
  };

  const applyCalculatedRR = useCallback((calcR: number) => {
    let matchedOptionValue: string | null = null;
    for (const opt of rrOptionsFromConfig) {
      const val = getOptionValue(opt);
      const parsed = parseRRNumber(val);
      if (parsed !== null && Math.abs(parsed - calcR) < 0.05) {
        matchedOptionValue = val;
        break;
      }
    }

    if (matchedOptionValue) {
      setRr(matchedOptionValue);
      sessionStorage.setItem(ssKey('rr'), matchedOptionValue);
    } else {
      let customVal: string | null = null;
      for (const opt of customRROptions) {
        const parsed = parseRRNumber(opt);
        if (parsed !== null && Math.abs(parsed - calcR) < 0.05) {
          customVal = opt;
          break;
        }
      }
      if (!customVal) {
        customVal = `1:${calcR}`;
        setCustomRROptions(prev => prev.includes(customVal!) ? prev : [...prev, customVal!]);
      }
      setRr(customVal);
      sessionStorage.setItem(ssKey('rr'), customVal);
    }
  }, [rrOptionsFromConfig, customRROptions, parseRRNumber, ssKey]);

  const tryAutoCalculate = useCallback((
    currPrice: string,
    currSl: string,
    currTp: string,
    currRr: string | null,
    changedField?: 'price' | 'sl' | 'tp' | 'rr'
  ) => {
    const pVal = currPrice.trim() !== '' ? parseFloat(currPrice) : NaN;
    const slVal = currSl.trim() !== '' ? parseFloat(currSl) : NaN;
    const tpVal = currTp.trim() !== '' ? parseFloat(currTp) : NaN;
    const rVal = parseRRNumber(currRr);

    const hasP = !isNaN(pVal);
    const hasSl = !isNaN(slVal);
    const hasTp = !isNaN(tpVal);
    const hasR = rVal !== null && rVal > 0;

    const filledCount = [hasP, hasSl, hasTp, hasR].filter(Boolean).length;

    // Case 1: Đúng 3 trường đã nhập -> tính trường còn lại
    if (filledCount === 3) {
      const precision = getPrecision(currPrice, currSl, currTp);

      // Thiếu TP -> tính TP: TP = Entry ± (R * Risk)
      if (hasP && hasSl && hasR && !hasTp) {
        const risk = Math.abs(pVal - slVal);
        if (risk > 0) {
          const reward = rVal * risk;
          const calcTp = isBuy ? (pVal + reward) : (pVal - reward);
          const tpStr = formatPrice(calcTp, precision);
          setTp(tpStr);
          sessionStorage.setItem(ssKey('tp'), tpStr);
        }
      }
      // Thiếu SL -> tính SL: SL = Entry ∓ (Reward / R)
      else if (hasP && hasTp && hasR && !hasSl) {
        const reward = Math.abs(tpVal - pVal);
        if (reward > 0 && rVal > 0) {
          const risk = reward / rVal;
          const calcSl = isBuy ? (pVal - risk) : (pVal + risk);
          const slStr = formatPrice(calcSl, precision);
          setSl(slStr);
          sessionStorage.setItem(ssKey('sl'), slStr);
        }
      }
      // Thiếu Entry Price -> tính Entry Price: Entry = (TP + R * SL) / (R + 1)
      else if (hasSl && hasTp && hasR && !hasP) {
        if (rVal > 0) {
          const calcP = (tpVal + rVal * slVal) / (rVal + 1);
          const pStr = formatPrice(calcP, precision);
          setPrice(pStr);
          sessionStorage.setItem(ssKey('price_base'), pStr);
        }
      }
      // Thiếu RR -> tính RR: R = |TP - Entry| / |Entry - SL|
      else if (hasP && hasSl && hasTp && !hasR) {
        const risk = Math.abs(pVal - slVal);
        const reward = Math.abs(tpVal - pVal);
        if (risk > 0) {
          const calcR = Math.round((reward / risk) * 100) / 100;
          if (calcR > 0) {
            applyCalculatedRR(calcR);
          }
        }
      }
    }
    // Case 2: Cả 4 trường đều có giá trị
    else if (filledCount === 4) {
      const precision = getPrecision(currPrice, currSl, currTp);
      if (changedField === 'rr') {
        const risk = Math.abs(pVal - slVal);
        if (risk > 0 && rVal) {
          const reward = rVal * risk;
          const calcTp = isBuy ? (pVal + reward) : (pVal - reward);
          const tpStr = formatPrice(calcTp, precision);
          setTp(tpStr);
          sessionStorage.setItem(ssKey('tp'), tpStr);
        }
      } else if (changedField === 'tp' || changedField === 'sl' || changedField === 'price') {
        const risk = Math.abs(pVal - slVal);
        const reward = Math.abs(tpVal - pVal);
        if (risk > 0) {
          const calcR = Math.round((reward / risk) * 100) / 100;
          if (calcR > 0) {
            applyCalculatedRR(calcR);
          }
        }
      }
    }
  }, [isBuy, parseRRNumber, applyCalculatedRR, ssKey]);

  const handleSelectRR = useCallback((val: string) => {
    setRr(val);
    sessionStorage.setItem(ssKey('rr'), val);
    tryAutoCalculate(price, sl, tp, val, 'rr');
  }, [price, sl, tp, ssKey, tryAutoCalculate]);

  useEffect(() => {
    isLoaded.current = false;
    // Luôn chạy load dù planned_setups null/undefined — setup sẽ là undefined → vào else branch load session
    const setup = journal_data?.planned_setups?.find(s => s.setup_type?.toUpperCase() === targetType);

    if (setup) {
      setId(setup.id);

      // Ưu tiên: session > API > ''
      const sessionPrice = readSetupSession('price_base');
      const sessionBand = readSetupSession('price_margin');

      if (sessionPrice !== null || sessionBand !== null) {
        setPrice(sessionPrice || '');
        setBand(sessionBand || '');
      } else if (setup.entry_zone) {
        const separator = setup.entry_zone.includes('±') ? '±' : '|';
        const parts = setup.entry_zone.split(separator);
        setPrice(parts[0]?.trim() || '');
        setBand(parts[1]?.trim() || '');
      } else if ((setup as any).price_base !== undefined && (setup as any).price_base !== null) {
        setPrice(String((setup as any).price_base || ''));
        setBand(String((setup as any).price_margin || ''));
      } else {
        setPrice('');
        setBand('');
      }

      const rrVal = setup.min_rr_ratio || (setup as any).rr_ratio;
      const sessionRr = readSetupSession('rr');
      setRr(sessionRr || rrVal || null);

      const defaultValues = rrOptionsFromConfig.map((opt: any) => getOptionValue(opt));
      const effectiveRr = sessionRr || rrVal;
      if (effectiveRr && !defaultValues.includes(effectiveRr)) {
        setCustomRROptions(prev => prev.includes(effectiveRr) ? prev : [...prev, effectiveRr]);
      }

      setSl(readSetupSession('sl') || (setup.estimated_sl ? String(setup.estimated_sl) : ''));
      setTp(readSetupSession('tp') || (setup.estimated_tp ? String(setup.estimated_tp) : ''));
      setTrigger(readSetupSession('trigger') || setup.trigger_condition || '');
      setCancel(readSetupSession('cancel') || setup.cancel_condition || '');
    } else {
      // Không có setup từ API (hoặc planned_setups null) → load hoàn toàn từ session
      setId(undefined);
      setPrice(readSetupSession('price_base') || '');
      setBand(readSetupSession('price_margin') || '');
      setRr(readSetupSession('rr') || null);
      setSl(readSetupSession('sl') || '');
      setTp(readSetupSession('tp') || '');
      setTrigger(readSetupSession('trigger') || '');
      setCancel(readSetupSession('cancel') || '');
    }

    const timer = setTimeout(() => { isLoaded.current = true; }, 50);
    return () => clearTimeout(timer);
  }, [journal_data, targetType, rrOptionsFromConfig, date, account_number, readSetupSession]);

  const reset = useCallback(() => {
    removeSetupSession();
    setPrice(''); setBand(''); setRr(null);
    setCustomRROptions([]); setIsAddingRR(false); setCustomRRValue('');
    setSl(''); setTp(''); setTrigger(''); setCancel('');
    setShowErrors(false);
  }, [removeSetupSession]);

  const validateAndHighlight = useCallback(() => {
    const isMissing = !price.trim() || !band.trim() || !rr || !sl.trim() || !tp.trim() || !trigger.trim() || !cancel.trim();
    if (isMissing) {
      setShowErrors(true);
      return false;
    }
    return true;
  }, [price, band, rr, sl, tp, trigger, cancel]);

  const save = useCallback(async () => {
    if (!isLoaded.current || saveStatus === 'saving') return false;
    if (!validateAndHighlight()) {
      message.warning(`Vui lòng điền đầy đủ các thông tin kế hoạch setup ${isBuy ? 'MUA' : 'BÁN'}!`);
      return false;
    }
    setSaveStatus('saving');
    try {
      const entryZoneVal = band.trim() ? `${price.trim()} ± ${band.trim()}` : price.trim();
      const payload: any = {
        id,
        setup_type: targetType as SetupType,
        entry_zone: entryZoneVal,
        min_rr_ratio: rr || undefined,
        price_base: price ? Number(price) : undefined,
        price_margin: band ? Number(band) : undefined,
        rr_ratio: rr || undefined,
        estimated_sl: sl ? Number(sl) : undefined,
        estimated_tp: tp ? Number(tp) : undefined,
        trigger_condition: String(trigger ?? '').trim() || undefined,
        cancel_condition: String(cancel ?? '').trim() || undefined,
      };
      const response = await updateDailyChecklist({
        date,
        setups: [payload]
      });

      if (response?.success && response?.journal?.planned_setups) {
        const currentSetup = response.journal.planned_setups.find(
          (s: any) => s.setup_type?.toUpperCase() === targetType
        );
        if (currentSetup?.id) {
          setId(currentSetup.id);
        }
      }
      setSaveStatus('saved');
      setShowErrors(false);
      removeSetupSession();
      onSaveSuccess?.();
      return true;
    } catch {
      setSaveStatus('error');
      return false;
    } finally {
      onReload?.();
    }
  }, [saveStatus, setSaveStatus, validateAndHighlight, id, targetType, price, band, rr, sl, tp, trigger, cancel, date, onSaveSuccess, onReload, isBuy, removeSetupSession]);

  const p = isBuy ? 'buy' : 'sell';
  const isOnSession = ['price_base', 'price_margin', 'rr', 'sl', 'tp', 'trigger', 'cancel'].some(
    f => sessionStorage.getItem(ssKey(f)) !== null || sessionStorage.getItem(`${p}_${f}_${date}_undefined_${account_number}_step1`) !== null
  );

  const accent = isBuy ? C.green : C.red;
  const isCompleted = !!(price.trim() && band.trim() && rr && sl.trim() && tp.trim() && trigger.trim() && cancel.trim());
  const hasData = !!(price.trim() || band.trim() || rr || sl.trim() || tp.trim() || trigger.trim() || cancel.trim());

  useEffect(() => {
    if (sectionRef) {
      sectionRef.current = {
        isUnsaved: () => {
          return ['price_base', 'price_margin', 'rr', 'sl', 'tp', 'trigger', 'cancel'].some(
            f => sessionStorage.getItem(ssKey(f)) !== null || sessionStorage.getItem(`${p}_${f}_${date}_undefined_${account_number}_step1`) !== null
          );
        },
        save: async () => {
          return await save();
        },
        isCompleted: () => isCompleted,
        hasData: () => hasData,
        isIncomplete: () => !isCompleted,
        validateAndHighlight: () => validateAndHighlight()
      };
    }
    return () => {
      if (sectionRef) sectionRef.current = null;
    };
  }, [sectionRef, ssKey, p, date, account_number, save, validateAndHighlight, isCompleted, hasData]);

  const tag = (
    <StatusChip tone={isOnSession ? 'gold' : (isCompleted ? 'green' : 'gray')} dot>
      {isOnSession ? 'Chưa lưu' : isCompleted ? 'Đã hoàn thành' : 'Chưa hoàn thành'}
    </StatusChip>
  );

  return (
    <CollapsibleSection num={num} title={title} chip={tag} open={open} onToggle={onToggle}>
      <div className="jd-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ border: `1px solid ${C.borderSoft}`, borderRadius: 12, padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14 }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: accent }} />
              <span style={{ fontSize: 13.5, fontWeight: 700, color: C.title }}>{ui_labels.price_zone_title}</span>
              {getTooltipContent(tooltips, isBuy ? TOOLTIP_CONFIG.startOfDay.buyEntryZone : TOOLTIP_CONFIG.startOfDay.sellEntryZone) && (
                <Tooltip
                  title={getTooltipContent(tooltips, isBuy ? TOOLTIP_CONFIG.startOfDay.buyEntryZone : TOOLTIP_CONFIG.startOfDay.sellEntryZone)?.text}
                  placement={getTooltipContent(tooltips, isBuy ? TOOLTIP_CONFIG.startOfDay.buyEntryZone : TOOLTIP_CONFIG.startOfDay.sellEntryZone)?.placement || "top"}
                >
                  <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
                </Tooltip>
              )}
            </div>
            <div className="jd-two-col" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 12 }}>
              <div>
                <FieldLabel style={showErrors && !price ? { color: '#ef4444' } : undefined}>{ui_labels.price_base}</FieldLabel>
                <TextInput
                  value={price}
                  onChange={e => {
                    setPrice(e.target.value);
                    sessionStorage.setItem(ssKey('price_base'), e.target.value);
                  }}
                  onBlur={e => {
                    if (showErrors && !price) e.currentTarget.style.borderColor = '#ef4444';
                    tryAutoCalculate(e.target.value, sl, tp, rr, 'price');
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      tryAutoCalculate((e.target as HTMLInputElement).value, sl, tp, rr, 'price');
                    }
                  }}
                  placeholder={isBuy ? 'VD: 2312.0' : 'VD: 2346.0'}
                  style={{
                    fontWeight: 700,
                    ...(showErrors && !price && { border: '1.5px solid #ef4444', boxShadow: '0 0 0 1px #ef4444' })
                  }}
                />
              </div>
              <div>
                <FieldLabel style={showErrors && !band ? { color: '#ef4444' } : undefined}>{ui_labels.price_margin}</FieldLabel>
                <TextInput
                  value={band}
                  onChange={e => {
                    setBand(e.target.value);
                    sessionStorage.setItem(ssKey('price_margin'), e.target.value);
                  }}
                  placeholder="VD: 1.5"
                  style={{
                    fontWeight: 700,
                    ...(showErrors && !band && { border: '1.5px solid #ef4444', boxShadow: '0 0 0 1px #ef4444' })
                  }}
                />
              </div>
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <FieldLabel style={{ fontSize: 13, color: showErrors && !rr ? '#ef4444' : C.title, margin: 0 }}>{rrRatioConfig?.field_label || 'Risk/Reward'}</FieldLabel>
              {getTooltipContent(tooltips, isBuy ? TOOLTIP_CONFIG.startOfDay.buyRrRatio : TOOLTIP_CONFIG.startOfDay.sellRrRatio) && (
                <Tooltip
                  title={getTooltipContent(tooltips, isBuy ? TOOLTIP_CONFIG.startOfDay.buyRrRatio : TOOLTIP_CONFIG.startOfDay.sellRrRatio)?.text}
                  placement={getTooltipContent(tooltips, isBuy ? TOOLTIP_CONFIG.startOfDay.buyRrRatio : TOOLTIP_CONFIG.startOfDay.sellRrRatio)?.placement || "top"}
                >
                  <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
                </Tooltip>
              )}
            </div>
            <div style={{
              display: 'flex', gap: 8, flexWrap: 'wrap',
              padding: showErrors && !rr ? '6px' : '0',
              borderRadius: 12,
              border: showErrors && !rr ? '1.5px solid #ef4444' : '1.5px solid transparent',
              transition: 'all .2s'
            }}>
              {rrOptionsFromConfig.map((opt: any) => {
                const val = getOptionValue(opt);
                const label = getOptionLabel(opt, currentLanguage);
                const type = opt.type || opt.kind;
                const on = rr === val;

                let c = isBuy
                  ? { bd: C.greenBd, bg: C.greenBg, tx: C.greenTx }
                  : { bd: C.redBd, bg: C.redBg, tx: C.redTx };

                if (type === 'primary') {
                  c = { bd: C.blue, bg: C.infoBg, tx: C.blue };
                }

                return (
                  <button
                    key={val}
                    onClick={() => handleSelectRR(val)}
                    style={{
                      flex: 1, minWidth: 60, padding: '9px 0', borderRadius: 9, fontFamily: 'inherit',
                      fontSize: 13, fontWeight: 700, cursor: 'pointer',
                      border: `1.5px solid ${on ? c.bd : C.chipOffBd}`,
                      background: on ? c.bg : C.surface,
                      color: on ? c.tx : C.onMuted
                    }}
                  >
                    {label}
                  </button>
                );
              })}

              {customRROptions.map(val => {
                const on = rr === val;
                const c = isBuy ? { bd: C.greenBd, bg: C.greenBg, tx: C.greenTx } : { bd: C.redBd, bg: C.redBg, tx: C.redTx };
                return (
                  <div key={val} style={{ position: 'relative', flex: 1, minWidth: 60 }} className="custom-rr-item">
                    <button onClick={() => handleSelectRR(val)} style={{ width: '100%', padding: '9px 0', borderRadius: 9, fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: `1.5px solid ${on ? c.bd : C.chipOffBd}`, background: on ? c.bg : C.surface, color: on ? c.tx : C.onMuted }}>
                      {val}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCustomRROptions(prev => prev.filter(x => x !== val));
                        if (rr === val) {
                          setRr(null);
                          sessionStorage.removeItem(ssKey('rr'));
                        }
                      }}
                      style={{
                        position: 'absolute', top: -6, right: -6, width: 16, height: 16, borderRadius: '50%',
                        background: C.red, color: C.white, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', fontSize: 10, fontWeight: 'bold', zIndex: 2,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      ✕
                    </button>
                  </div>
                );
              })}

              {isAddingRR ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: `1.5px solid ${C.blue}`, borderRadius: 9, padding: '4px 10px', background: C.surface }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.title }}>1 :</span>
                  <input
                    autoFocus
                    type="text"
                    value={customRRValue}
                    onChange={e => setCustomRRValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        if (customRRValue.trim()) {
                          const val = `1:${customRRValue.trim()}`;
                          setCustomRROptions(prev => prev.includes(val) ? prev : [...prev, val]);
                          handleSelectRR(val);
                          setIsAddingRR(false);
                          setCustomRRValue('');
                        }
                      } else if (e.key === 'Escape') {
                        setIsAddingRR(false);
                        setCustomRRValue('');
                      }
                    }}
                    style={{
                      width: 50, border: 'none', borderBottom: `1px solid ${C.border}`,
                      padding: '2px 0', fontSize: 13, fontWeight: 700, textAlign: 'center',
                      background: 'transparent', outline: 'none'
                    }}
                  />
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      onClick={() => {
                        if (customRRValue.trim()) {
                          const val = `1:${customRRValue.trim()}`;
                          setCustomRROptions(prev => prev.includes(val) ? prev : [...prev, val]);
                          handleSelectRR(val);
                          setIsAddingRR(false);
                          setCustomRRValue('');
                        }
                      }}
                      style={{ background: 'none', border: 'none', color: C.green, cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    </button>
                    <button
                      onClick={() => { setIsAddingRR(false); setCustomRRValue(''); }}
                      style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setIsAddingRR(true)}
                  style={{
                    flex: 1, minWidth: 100, border: `1.5px dashed ${C.border}`, borderRadius: 9,
                    padding: '8px 0', background: C.surface, color: 'gray',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5
                  }}
                >
                  <Plus size={14} /> {t('Thêm RR')}
                </button>
              )}
            </div>
            <div style={{ fontSize: 12, color: C.faint, marginTop: 6, fontStyle: 'italic' }}>
              {t('Chạm để chọn mức RR áp dụng. Nhập đủ 3 trong 4 trường (Giá vào lệnh, SL, TP, RR) sẽ tự động tính trường còn lại.')}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <PriceField
              label={ui_labels.sl_placeholder}
              prefix="SL"
              prefixColor={C.red}
              value={sl}
              onChange={(v) => {
                setSl(v);
                sessionStorage.setItem(ssKey('sl'), v);
              }}
              onBlur={e => {
                tryAutoCalculate(price, e.target.value, tp, rr, 'sl');
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  tryAutoCalculate(price, (e.target as HTMLInputElement).value, tp, rr, 'sl');
                }
              }}
              placeholder={isBuy ? 'VD: 2305.0' : 'VD: 2353.0'}
              tooltipKey={isBuy ? TOOLTIP_CONFIG.startOfDay.buySl : TOOLTIP_CONFIG.startOfDay.sellSl}
              tooltips={tooltips}
              hasError={showErrors && !sl}
            />
            <PriceField
              label={ui_labels.tp_placeholder}
              prefix="TP"
              prefixColor={C.green}
              value={tp}
              onChange={(v) => {
                setTp(v);
                sessionStorage.setItem(ssKey('tp'), v);
              }}
              onBlur={e => {
                tryAutoCalculate(price, sl, e.target.value, rr, 'tp');
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  tryAutoCalculate(price, sl, (e.target as HTMLInputElement).value, rr, 'tp');
                }
              }}
              placeholder={isBuy ? 'VD: 2330.0' : 'VD: 2328.0'}
              tooltipKey={isBuy ? TOOLTIP_CONFIG.startOfDay.buyTp : TOOLTIP_CONFIG.startOfDay.sellTp}
              tooltips={tooltips}
              hasError={showErrors && !tp}
            />
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <FieldLabel style={{ fontSize: 13, color: showErrors && !trigger.trim() ? '#ef4444' : C.title, margin: 0 }}>{ui_labels.trigger}</FieldLabel>
              {getTooltipContent(tooltips, isBuy ? TOOLTIP_CONFIG.startOfDay.buyTrigger : TOOLTIP_CONFIG.startOfDay.sellTrigger) && (
                <Tooltip
                  title={getTooltipContent(tooltips, isBuy ? TOOLTIP_CONFIG.startOfDay.buyTrigger : TOOLTIP_CONFIG.startOfDay.sellTrigger)?.text}
                  placement={getTooltipContent(tooltips, isBuy ? TOOLTIP_CONFIG.startOfDay.buyTrigger : TOOLTIP_CONFIG.startOfDay.sellTrigger)?.placement || "top"}
                >
                  <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
                </Tooltip>
              )}
            </div>
            <TextArea value={trigger} onChange={e => {
              setTrigger(e.target.value);
              sessionStorage.setItem(ssKey('trigger'), e.target.value);
            }} style={{
              minHeight: 120,
              ...(showErrors && !trigger.trim() && { border: '1.5px solid #ef4444', boxShadow: '0 0 0 1px #ef4444' })
            }}
              placeholder={isBuy ? 'VD: Nến H1 đóng cửa trên vùng mua + xuất hiện mô hình đảo chiều, volume tăng…' : 'VD: Nến H1 đóng cửa dưới vùng bán + xuất hiện mô hình đảo chiều giảm, volume tăng…'} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={showErrors && !cancel.trim() ? '#ef4444' : C.redTx} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M15 9l-6 6M9 9l6 6" /></svg>
              <span style={{ fontSize: 13, fontWeight: 600, color: showErrors && !cancel.trim() ? '#ef4444' : C.redTx }}>{ui_labels.cancel_condition}</span>
              {getTooltipContent(tooltips, isBuy ? TOOLTIP_CONFIG.startOfDay.buyCancel : TOOLTIP_CONFIG.startOfDay.sellCancel) && (
                <Tooltip
                  title={getTooltipContent(tooltips, isBuy ? TOOLTIP_CONFIG.startOfDay.buyCancel : TOOLTIP_CONFIG.startOfDay.sellCancel)?.text}
                  placement={getTooltipContent(tooltips, isBuy ? TOOLTIP_CONFIG.startOfDay.buyCancel : TOOLTIP_CONFIG.startOfDay.sellCancel)?.placement || "top"}
                >
                  <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
                </Tooltip>
              )}
            </div>
            <TextArea value={cancel} onChange={e => {
              setCancel(e.target.value);
              sessionStorage.setItem(ssKey('cancel'), e.target.value);
            }} style={{
              minHeight: 120,
              border: showErrors && !cancel.trim() ? '1.5px solid #ef4444' : `1px solid ${C.warnBd}`,
              background: C.cancelBg,
              ...(showErrors && !cancel.trim() && { boxShadow: '0 0 0 1px #ef4444' })
            }}
              placeholder={isBuy ? 'VD: Giá phá vỡ vùng hỗ trợ cốt lõi, đóng cửa H1 dưới SL kế hoạch…' : 'VD: Giá phá vỡ vùng kháng cự cốt lõi, đóng cửa H1 trên SL kế hoạch…'} />
          </div>
        </div>
      </div>
      {
        can_edit ? (
          <SaveResetFooter saveStatus={saveStatus} onReset={reset} onSave={save} />
        )
          : (
            null
          )
      }
    </CollapsibleSection>
  );
}

interface Scenario {
  id: number;
  dbId?: number;
  cond: string;
  act: string;
  prob: number;
  dir?: string;
  img?: string;
}

interface ScenarioSectionProps {
  date: string;
  journal_data: Journal;
  form_configs: FormFieldConfig[];
  ui_labels: UILabels_DailyPlan['step_7'];
  account_number: string;
  onReload?: () => void;
  open: boolean;
  can_edit?: boolean;
  onToggle: () => void;
  onSaveSuccess?: () => void;
  tooltips?: PostTradeTooltipData | null;
  sectionRef?: React.MutableRefObject<SectionHandle | null>;
}

function ScenarioSection({ date, journal_data, account_number, form_configs, ui_labels, onReload, open, can_edit, onToggle, onSaveSuccess, tooltips, sectionRef }: ScenarioSectionProps) {
  const [scenarios, setScenarios] = useState<Scenario[]>([
    { id: 1, cond: '', act: '', prob: 0, dir: 'neutral' }
  ]);
  const [expandedIds, setExpandedIds] = useState<number[]>([1]);
  const [showErrors, setShowErrors] = useState(false);
  const nextId = useRef(2);

  const [saveStatus, setSaveStatus] = useSaveStatus(1500);
  const isLoaded = useRef(false);
  const currentLanguage = i18next.language;

  const toggleExpand = useCallback((id: number) => {
    setExpandedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);

  const scenarioActionConfig = useMemo(() =>
    getFormConfigsField('field_key', 'scenario_action', form_configs),
    [form_configs]
  );

  const scenarioActionOptions = useMemo(() => scenarioActionConfig?.options || [
    { label_en: 'BUY', label_vi: 'Mua', value: 'BUY', type: 'good' },
    { label_en: 'SELL', label_vi: 'Bán', value: 'SELL', type: 'bad' },
    { label_en: 'NEUTRAL', label_vi: 'Trung lập', value: 'NEUTRAL', type: 'secondary' }
  ], [scenarioActionConfig]);

  // Helper đọc sessionStorage cho ScenarioSection
  const readScenarioSession = (): Scenario[] | null => {
    const raw = sessionStorage.getItem(`scenarios_${date}_${journal_data.id}_${account_number}_step1`);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  };

  const removeScenarioSession = () => {
    sessionStorage.removeItem(`scenarios_${date}_${journal_data.id}_${account_number}_step1`);
    sessionStorage.removeItem(`condition_text_${date}_${journal_data.id}_${account_number}_step1`);
    sessionStorage.removeItem(`action_text_${date}_${journal_data.id}_${account_number}_step1`);
    sessionStorage.removeItem(`probability_${date}_${journal_data.id}_${account_number}_step1`);
    sessionStorage.removeItem(`direction_${date}_${journal_data.id}_${account_number}_step1`);
  };

  useEffect(() => {
    if (journal_data?.planned_setups) {
      isLoaded.current = false;
      const apiScenarios = journal_data.planned_setups.filter(s => s.setup_type === 'SCENARIO');

      if (apiScenarios.length > 0) {
        const mapped: Scenario[] = apiScenarios.map((s, idx) => ({
          id: idx + 1,
          dbId: s.id,
          cond: s.price_behavior_desc || '',
          act: s.action_notes || '',
          prob: s.probability_percent || 0,
          dir: (s.action_direction?.toLowerCase() || 'neutral'),
          img: s.chart_image_url || undefined
        }));

        setScenarios(mapped);
        nextId.current = mapped.length + 1;
      } else {
        // Không có từ API → fallback về sessionStorage
        const sessionScenarios = readScenarioSession();
        if (sessionScenarios && sessionScenarios.length > 0) {
          setScenarios(sessionScenarios);
          nextId.current = Math.max(...sessionScenarios.map(s => s.id), 1) + 1;
        } else {
          setScenarios([
            { id: 1, cond: '', act: '', prob: 0, dir: 'neutral' }
          ]);
          nextId.current = 2;
        }
      }

      setTimeout(() => { isLoaded.current = true; }, 50);
    }
  }, [journal_data, date]);

  const reset = useCallback(() => {
    setScenarios([
      { id: 1, cond: '', act: '', prob: 0, dir: 'neutral' }
    ]);
    nextId.current = 2;
    setShowErrors(false);
  }, []);

  const validateAndHighlight = useCallback(() => {
    const incomplete = scenarios.some(sc => !sc.cond.trim() || !sc.act.trim());
    if (incomplete || scenarios.length === 0) {
      setShowErrors(true);
      const incompleteIds = scenarios.filter(sc => !sc.cond.trim() || !sc.act.trim()).map(sc => sc.id);
      setExpandedIds(prev => Array.from(new Set([...prev, ...incompleteIds])));
      return false;
    }
    return true;
  }, [scenarios]);

  const save = useCallback(async () => {
    if (!isLoaded.current || saveStatus === 'saving') return;
    if (!validateAndHighlight()) {
      message.warning('Vui lòng điền đầy đủ mô tả kịch bản và ghi chú hành động!');
      return false;
    }
    setSaveStatus('saving');

    const payloadSetups = scenarios.map((sc, idx) => {
      const baseItem: any = {
        setup_type: 'SCENARIO' as SetupType,
        scenario_name: `Kịch bản ${idx + 1}`,
        scenario_desc: sc.cond,
        scenario_action: sc.dir?.toUpperCase() || 'NEUTRAL',
        action_notes: sc.act,
        probability_percent: sc.prob,
        chart_image_url: sc.img,
      };

      if (sc.dbId) {
        baseItem.id = sc.dbId;
      }

      return baseItem;
    });

    try {
      await updateDailyChecklist({ date, setups: payloadSetups });
      onReload?.();
      setSaveStatus('saved');
      setShowErrors(false);
      removeScenarioSession();
      onSaveSuccess?.();
      return true;
    } catch {
      setSaveStatus('error');
      return false;
    }
  }, [saveStatus, setSaveStatus, validateAndHighlight, scenarios, date, onReload, onSaveSuccess]);

  const saveScenarioSession = (next: Scenario[]) =>
    sessionStorage.setItem(`scenarios_${date}_${journal_data.id}_${account_number}_step1`, JSON.stringify(next));

  useEffect(() => {
    if (sectionRef) {
      sectionRef.current = {
        isUnsaved: () => {
          return readScenarioSession() !== null;
        },
        save: async () => {
          return await save();
        },
        isIncomplete: () => {
          const totalScs = scenarios.length;
          const completedScs = scenarios.filter(sc => sc.cond.trim() && sc.act.trim()).length;
          return totalScs === 0 || completedScs < totalScs;
        },
        validateAndHighlight: () => validateAndHighlight()
      };
    }
    return () => {
      if (sectionRef) sectionRef.current = null;
    };
  }, [sectionRef, readScenarioSession, save, validateAndHighlight, scenarios]);

  const updateLocalTyping = useCallback((id: number, patch: Partial<Scenario>) => {
    setScenarios(s => {
      const next = s.map(x => x.id === id ? { ...x, ...patch } : x);
      saveScenarioSession(next);
      return next;
    });
  }, [date, journal_data.id, account_number]);

  const removeScenario = useCallback((id: number) => {
    setScenarios(s => {
      const next = s.filter(x => x.id !== id);
      saveScenarioSession(next);
      return next;
    });
  }, [date, journal_data.id, account_number]);

  const totalScs = scenarios.length;
  const completedScs = scenarios.filter(sc => sc.cond.trim() && sc.act.trim()).length;
  const isOnSession = readScenarioSession() !== null;

  let chipTone: 'gray' | 'gold' | 'green' = 'gray';
  let chipLabel = 'Chưa hoàn thành';

  if (completedScs === 0) {
    chipTone = isOnSession ? 'gold' : 'gray';
    chipLabel = isOnSession ? 'Chưa lưu' : 'Chưa hoàn thành';
  } else if (completedScs < totalScs) {
    chipTone = isOnSession ? 'green' : 'gold';
    chipLabel = isOnSession ? `Đang chỉnh sửa (${completedScs}/${totalScs} kịch bản)` : `Đang triển khai (${completedScs}/${totalScs} kịch bản)`;
  } else {
    chipTone = isOnSession ? 'gold' : 'green';
    chipLabel = isOnSession ? `Đang chỉnh sửa (${completedScs}/${totalScs} kịch bản)` : `Đã hoàn thành (${completedScs}/${totalScs} kịch bản)`;
  }

  return (
    <CollapsibleSection num={7} title="Dự báo kịch bản phiên" open={open} onToggle={onToggle}
      chip={<StatusChip tone={chipTone} dot>{chipLabel}</StatusChip>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {scenarios.map((sc, i) => {
          const pal = PALETTE[i % PALETTE.length];
          const isMain = i === 0;
          const isScMissing = showErrors && (!sc.cond.trim() || !sc.act.trim());

          return (
            <div key={sc.id} style={{
              border: isScMissing ? '1.5px solid #ef4444' : `1px solid ${C.borderSoft}`,
              borderRadius: 13,
              overflow: 'hidden',
              transition: 'all .2s'
            }}>
              <div
                onClick={() => toggleExpand(sc.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '12px 16px', background: isMain ? pal[1] : 'var(--jd-surface-3)', cursor: 'pointer' }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 38, padding: '4px 9px', borderRadius: 8, fontSize: 13, fontWeight: 800, background: pal[1], border: `1px solid ${pal[2]}`, color: pal[3] }}>KB{i + 1}</span>
                <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: isScMissing ? '#ef4444' : C.title }}>{isMain ? 'Kịch bản chính' : `Kịch bản ${i + 1}`}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: C.labelStrong }}>
                  {sc.prob}%<span style={{ width: 54, height: 6, borderRadius: 99, background: C.trackSoft, overflow: 'hidden', display: 'inline-block' }}><span style={{ display: 'block', height: '100%', borderRadius: 99, background: pal[0], width: `${sc.prob}%` }} /></span>
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {scenarios.length > 1 && (
                    <button onClick={(e) => { e.stopPropagation(); removeScenario(sc.id); }} style={{ width: 28, height: 28, flex: 'none', borderRadius: 8, border: `1px solid ${C.iconBtnBd}`, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: C.faint }}><Trash2 size={14} /></button>
                  )}
                  {expandedIds.includes(sc.id) ? <ChevronUp size={20} color={C.muted} /> : <ChevronDown size={20} color={C.muted} />}
                </div>
              </div>
              {expandedIds.includes(sc.id) && (
                <div className="jd-two-col" style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, borderTop: `1px solid ${C.divider}` }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <FieldLabel style={{ fontSize: 13, margin: 0, color: showErrors && !sc.cond.trim() ? '#ef4444' : undefined }}>{ui_labels.if_price_does}</FieldLabel>
                      {getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.scenarioDesc) && (
                        <Tooltip
                          title={getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.scenarioDesc)?.text}
                          placement={getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.scenarioDesc)?.placement || "top"}
                        >
                          <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
                        </Tooltip>
                      )}
                    </div>
                    <TextArea
                      value={sc.cond}
                      onChange={e => updateLocalTyping(sc.id, { cond: e.target.value })}
                      style={{
                        minHeight: 62,
                        fontSize: 13.5,
                        ...(showErrors && !sc.cond.trim() && { border: '1.5px solid #ef4444', boxShadow: '0 0 0 1px #ef4444' })
                      }}
                      placeholder="VD: Giá hồi về vùng hỗ trợ 2310 ± 2 và giữ vững, H1 cho tín hiệu đảo chiều tăng…"
                    />
                  </div>

                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <FieldLabel style={{ fontSize: 13, margin: 0 }}>Hành động</FieldLabel>
                      {getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.scenarioAction) && (
                        <Tooltip
                          title={getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.scenarioAction)?.text}
                          placement={getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.scenarioAction)?.placement || "top"}
                        >
                          <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
                        </Tooltip>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 7, marginBottom: 9, flexWrap: 'wrap' }}>
                      {scenarioActionOptions.map((opt: any) => {
                        const val = getOptionValue(opt);
                        const label = getOptionLabel(opt, currentLanguage);
                        const type = opt.type || opt.kind || (val === 'buy' || val === 'BUY' || label === 'Mua' ? 'pos' : val === 'sell' || val === 'SELL' || label === 'Bán' ? 'neg' : 'neutral');
                        const isSelected = sc.dir === val || sc.dir === val.toLowerCase();

                        const c = type === 'BUY' || type === 'good'
                          ? { bd: C.green, bg: C.greenBg, tx: C.greenTx }
                          : type === 'SELL' || type === 'bad'
                            ? { bd: C.red, bg: C.redBg, tx: C.redTx }
                            : { bd: C.warnBd, bg: C.warnBg, tx: C.goldDark };

                        return (
                          <button
                            key={val}
                            onClick={() => updateLocalTyping(sc.id, { dir: val })}
                            style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, flex: 1, padding: '9px 0', borderRadius: 9,
                              fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                              border: `1.5px solid ${isSelected ? c.bd : C.chipOffBd}`, background: isSelected ? c.bg : C.surface, color: isSelected ? c.tx : C.onMuted,
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    <TextArea
                      value={sc.act}
                      onChange={e => updateLocalTyping(sc.id, { act: e.target.value })}
                      style={{
                        minHeight: 48,
                        fontSize: 13.5,
                        ...(showErrors && !sc.act.trim() && { border: '1.5px solid #ef4444', boxShadow: '0 0 0 1px #ef4444' })
                      }}
                      placeholder={ui_labels.action_notes}
                    />
                  </div>

                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <FieldLabel style={{ fontSize: 13, margin: 0 }}>{ui_labels.probability}</FieldLabel>
                      {getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.scenarioProbability) && (
                        <Tooltip
                          title={getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.scenarioProbability)?.text}
                          placement={getTooltipContent(tooltips, TOOLTIP_CONFIG.startOfDay.scenarioProbability)?.placement || "top"}
                        >
                          <InfoIcon size={14} style={{ cursor: 'pointer', color: 'oklch(0.55 0.01 90)' }} />
                        </Tooltip>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
                      <input type="range" min={0} max={100} step={5} value={sc.prob}
                        onChange={e => updateLocalTyping(sc.id, { prob: parseInt(e.target.value, 10) })}
                        style={{ flex: 1, accentColor: C.gold, cursor: 'pointer' }}
                      />
                      <span style={{ width: 48, textAlign: 'right', fontSize: 15, fontWeight: 800, color: C.title }}>{sc.prob}%</span>
                    </div>
                    <FieldLabel style={{ fontSize: 13, margin: '14px 0 7px' }}>{ui_labels.upload_chart}</FieldLabel>
                    <ScenarioImage img={sc.img}
                      onUpload={img => updateLocalTyping(sc.id, { img })}
                      onClear={() => updateLocalTyping(sc.id, { img: undefined })}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}

        <button onClick={() => {
          const id = nextId.current++;
          setScenarios(s => [...s, { id, cond: '', act: '', prob: 0, dir: 'neutral' }]);
          setExpandedIds(prev => [...prev, id]);
        }}
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 12, border: `1.5px dashed ${C.addDashedBd}`, borderRadius: 12, background: 'var(--jd-surface-2)', color: C.goldDark, fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>
          <Save size={16} /> Thêm kịch bản
        </button>
      </div>
      {
        can_edit ? (
          <SaveResetFooter saveStatus={saveStatus} onReset={reset} onSave={save} />
        )
          : (
            null
          )
      }
    </CollapsibleSection>
  );
}

// ==========================================
// MAIN STEP CONTAINER
// ==========================================

interface StartOfDayProps {
  date: string;
  account_number: string;
  symbol: string;
  journal_data: Journal;
  form_configs: FormFieldConfig[];
  ui_labels: UILabels_DailyPlan;
  can_edit?: boolean;
  onReload?: () => void;
  tooltips?: PostTradeTooltipData | null;
}

const StepStartOfDay = forwardRef<StepRefHandle, StartOfDayProps>(function StepStartOfDay(
  { date, account_number, symbol, journal_data, form_configs, ui_labels, can_edit, onReload, tooltips: propTooltips }: StartOfDayProps,
  ref
) {
  const [tooltips, setTooltips] = useState<PostTradeTooltipData | null>(propTooltips || null);
  const [activeSection, setActiveSection] = useState<number | null>(1);

  const riskSectionRef = useRef<SectionHandle | null>(null);
  const healthSectionRef = useRef<SectionHandle | null>(null);
  const marketSectionRef = useRef<SectionHandle | null>(null);
  const setupBuySectionRef = useRef<SectionHandle | null>(null);
  const setupSellSectionRef = useRef<SectionHandle | null>(null);
  const scenarioSectionRef = useRef<SectionHandle | null>(null);

  const getSectionRef = (num: number | null): SectionHandle | null => {
    switch (num) {
      case 1: return riskSectionRef.current;
      case 2: return healthSectionRef.current;
      case 3: return marketSectionRef.current;
      case 5: return setupBuySectionRef.current;
      case 6: return setupSellSectionRef.current;
      case 7: return scenarioSectionRef.current;
      default: return null;
    }
  };

  useImperativeHandle(ref, () => ({
    hasUnsavedChanges: () => {
      const allRefs = [
        riskSectionRef.current,
        healthSectionRef.current,
        marketSectionRef.current,
        setupBuySectionRef.current,
        setupSellSectionRef.current,
        scenarioSectionRef.current,
      ];
      return allRefs.some(sec => sec?.isUnsaved());
    },
    saveChanges: async () => {
      const allRefs = [
        riskSectionRef.current,
        healthSectionRef.current,
        marketSectionRef.current,
        setupBuySectionRef.current,
        setupSellSectionRef.current,
        scenarioSectionRef.current,
      ];
      let allSuccess = true;
      for (const sec of allRefs) {
        if (sec?.isUnsaved()) {
          // Bỏ qua các section chưa hoàn thành vì không thể gửi dữ liệu thiếu lên API
          // (Dữ liệu tạm của section đó vẫn an toàn trong sessionStorage)
          if (sec.isIncomplete?.()) {
            continue;
          }
          const res = await sec.save();
          if (res === false) {
            allSuccess = false;
          }
        }
      }
      return allSuccess;
    },
    getIncompleteSections: () => {
      const missing: IncompleteSection[] = [];
      if (riskSectionRef.current?.isIncomplete?.()) {
        missing.push({ num: 1, title: 'Mục tiêu & Quản trị rủi ro' });
      }
      if (healthSectionRef.current?.isIncomplete?.()) {
        missing.push({ num: 2, title: 'Kiểm tra thể trạng & Sức khỏe tâm lý' });
      }
      if (marketSectionRef.current?.isIncomplete?.()) {
        missing.push({ num: 3, title: 'Bối cảnh thị trường' });
      }

      if (setupBuySectionRef.current?.isIncomplete?.()) {
        missing.push({ num: 5, title: 'Kế hoạch Setup MUA (Buy)' });
      }
      if (setupSellSectionRef.current?.isIncomplete?.()) {
        missing.push({ num: 6, title: 'Kế hoạch Setup BÁN (Sell)' });
      }

      if (scenarioSectionRef.current?.isIncomplete?.()) {
        missing.push({ num: 7, title: 'Dự báo kịch bản phiên' });
      }
      return missing;
    },
    validateAndHighlight: () => {
      let firstIncomplete: number | null = null;
      let isValid = true;

      if (riskSectionRef.current?.isIncomplete?.()) {
        riskSectionRef.current?.validateAndHighlight?.();
        if (firstIncomplete === null) firstIncomplete = 1;
        isValid = false;
      }
      if (healthSectionRef.current?.isIncomplete?.()) {
        healthSectionRef.current?.validateAndHighlight?.();
        if (firstIncomplete === null) firstIncomplete = 2;
        isValid = false;
      }
      if (marketSectionRef.current?.isIncomplete?.()) {
        marketSectionRef.current?.validateAndHighlight?.();
        if (firstIncomplete === null) firstIncomplete = 3;
        isValid = false;
      }

      if (setupBuySectionRef.current?.isIncomplete?.()) {
        setupBuySectionRef.current?.validateAndHighlight?.();
        if (firstIncomplete === null) firstIncomplete = 5;
        isValid = false;
      }
      if (setupSellSectionRef.current?.isIncomplete?.()) {
        setupSellSectionRef.current?.validateAndHighlight?.();
        if (firstIncomplete === null) firstIncomplete = 6;
        isValid = false;
      }

      if (scenarioSectionRef.current?.isIncomplete?.()) {
        scenarioSectionRef.current?.validateAndHighlight?.();
        if (firstIncomplete === null) firstIncomplete = 7;
        isValid = false;
      }

      if (firstIncomplete !== null) {
        setActiveSection(firstIncomplete);
      }
      return isValid;
    }
  }));

  useEffect(() => {
    if (propTooltips) {
      setTooltips(propTooltips);
    } else {
      getToolTip().then((res) => {
        if (res?.success && res.data) {
          setTooltips(res.data);
        }
      }).catch(err => console.error("Lỗi khi tải Tooltips:", err));
    }
  }, [propTooltips]);

  const isSwitchingRef = useRef(false);

  const handleToggle = useCallback(async (num: number) => {
    if (isSwitchingRef.current) return;

    if (activeSection !== null) {
      const currentRef = getSectionRef(activeSection);
      if (currentRef) {
        const isIncomplete = currentRef.isIncomplete?.();
        // Chỉ cảnh báo khi người dùng bấm tiến lên section phía sau (num > activeSection)
        // Khi quay lại section trước (num < activeSection) hoặc đóng, luôn cho phép chuyển
        if (activeSection !== num && num > activeSection) {
          if (isIncomplete) {
            currentRef.validateAndHighlight?.();
            message.warning('Vui lòng hoàn thành các thông tin còn thiếu ở phần hiện tại trước khi chuyển!');
            return;
          }
        }

        if (!isIncomplete && (!currentRef.isUnsaved || currentRef.isUnsaved())) {
          try {
            isSwitchingRef.current = true;
            const res = await currentRef.save();
            if (res === false && activeSection !== num && num > activeSection) {
              return;
            }
          } catch (e) {
            console.error("Auto save error on section change:", e);
            if (activeSection !== num && num > activeSection) return;
          } finally {
            isSwitchingRef.current = false;
          }
        }
      }
    }

    setActiveSection(prev => (prev === num ? null : num));
  }, [activeSection]);

  const handleSaveNext = useCallback((currentNum: number) => {
    if (isSwitchingRef.current) return;
    setActiveSection(currentNum + 1);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <RiskSection
        onReload={onReload}
        date={date}
        account_number={account_number}
        can_edit={can_edit}
        journal_data={journal_data}
        form_configs={form_configs}
        ui_labels={ui_labels.step_1}
        open={activeSection === 1}
        onToggle={() => handleToggle(1)}
        onSaveSuccess={() => handleSaveNext(1)}
        tooltips={tooltips}
        sectionRef={riskSectionRef}
      />

      <HealthSection
        onReload={onReload}
        date={date}
        account_number={account_number}
        can_edit={can_edit}
        journal_data={journal_data}
        form_configs={form_configs}
        ui_labels={ui_labels.step_2}
        open={activeSection === 2}
        onToggle={() => handleToggle(2)}
        onSaveSuccess={() => handleSaveNext(2)}
        tooltips={tooltips}
        sectionRef={healthSectionRef}
      />

      <MarketSection
        onReload={onReload}
        date={date}
        account_number={account_number}
        can_edit={can_edit}
        symbol={symbol}
        journal_data={journal_data}
        form_configs={form_configs}
        ui_labels={ui_labels.step_3}
        open={activeSection === 3}
        onToggle={() => handleToggle(3)}
        onSaveSuccess={() => handleSaveNext(3)}
        tooltips={tooltips}
        sectionRef={marketSectionRef}
      />

      <NewsSection
        onReload={onReload}
        date={date}
        journal_data={journal_data}
        form_configs={form_configs}
        ui_labels={ui_labels.step_4}
        can_edit={can_edit}
        open={activeSection === 4}
        onToggle={() => handleToggle(4)}
        onSaveSuccess={() => handleSaveNext(4)}
        tooltips={tooltips}
      />

      <SetupSection
        onReload={onReload}
        date={date}
        account_number={account_number}
        can_edit={can_edit}
        num={5}
        side="buy"
        journal_data={journal_data}
        form_configs={form_configs}
        ui_labels={ui_labels.step_5}
        title="Kế hoạch Setup MUA (Buy)"
        open={activeSection === 5}
        onToggle={() => handleToggle(5)}
        onSaveSuccess={() => handleSaveNext(5)}
        tooltips={tooltips}
        sectionRef={setupBuySectionRef}
      />

      <SetupSection
        onReload={onReload}
        date={date}
        account_number={account_number}
        can_edit={can_edit}
        num={6}
        side="sell"
        journal_data={journal_data}
        form_configs={form_configs}
        ui_labels={ui_labels.step_6}
        title="Kế hoạch Setup BÁN (Sell)"
        open={activeSection === 6}
        onToggle={() => handleToggle(6)}
        onSaveSuccess={() => handleSaveNext(6)}
        tooltips={tooltips}
        sectionRef={setupSellSectionRef}
      />

      <ScenarioSection
        onReload={onReload}
        date={date}
        account_number={account_number}
        can_edit={can_edit}
        journal_data={journal_data}
        form_configs={form_configs}
        ui_labels={ui_labels.step_7}
        open={activeSection === 7}
        onToggle={() => handleToggle(7)}
        onSaveSuccess={() => handleSaveNext(7)}
        tooltips={tooltips}
        sectionRef={scenarioSectionRef}
      />
    </div>
  );
});

export default StepStartOfDay;